// NotesApp source file: home.component.ts

import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, HostListener } from '@angular/core';
import { ElementRef, ChangeDetectorRef, NgZone, TemplateRef, ViewContainerRef, computed, inject } from '@angular/core';
import { NoteSizeService } from '../../_service/note-size.service';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { filter, take, switchMap, takeUntil, catchError, distinctUntilChanged } from 'rxjs/operators';
import { Observable, Subscription, Subject, of, firstValueFrom } from 'rxjs';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { Menu } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SplitButtonModule } from 'primeng/splitbutton';
import { SliderModule } from 'primeng/slider';
import { SplitterModule } from 'primeng/splitter';
import { PopoverModule } from 'primeng/popover';

// Notes-App Services
import { ApiResponse, Note, Project, Status, NoteType, DataService, Tag } from '../../_service/data.service';
import { Space } from '../../_service/space.service';
import { SpaceDirectoryService } from '../../_service/space-directory.service';
import { BulkStatusResult, BulkDeleteResult, BulkTagAddResult, BulkTagRemoveResult } from '../../_service/data.service';
import { FilterParams, FilterService } from '../../_service/filter.service';
import { AuthService } from '../../_service/auth.service';
import { LayoutService } from '../../_service/layout.service';
//import { OLD__UserSettingsService } from '../../_service/OLD_usersettings.service';
import { EnvironmentService } from '../../_service/sync/environment.service';
import { AlertService } from '../../_service/alert.service';
import { LogService } from '../../_service/log.service';
import { SyncCoordinatorService } from '../../_service/sync/sync-coordinator.service';
import { RemoteChangesService } from '../../_service/sync/remote-changes.service';
import { EnvBadgesComponent } from '../env-badges/env-badges.component';
import { DataTargetService } from '../../_service/data-target.service';
import { getEnvironmentInfo } from '../../_util/environment-info';
import { AppVersionInfo, AppVersionService } from '../../_service/app-version.service';
import { AppUpdateService } from '../../_service/app-update.service';

import { AlertComponent } from '../alert/alert.component';
import { DialogType, GlobalDialogService } from '../../_service/global-dialog.service';
import { GlobalDialogComponent } from '../global-dialog/global-dialog.component';
import Delta from 'quill-delta';

import { BulkActionConfig, BulkActionDialogComponent, BulkActionRequest, BulkTagOption, BulkTagOptionGroup, ExportFormat } from './bulk-action-dialog/bulk-action-dialog.component';
import { HeaderComponent } from './header/header.component';
import { ProjectCardsComponent } from './project-cards/project-cards.component';
import { TagCardsComponent } from './tag-cards/tag-cards.component';
import { ListComponent, NoteTagChange, readListSortState } from './list/list.component';
import { CalendarViewComponent } from './calendar/calendar.component';
import { TaskListComponent } from './task-list/task-list.component';
import { QuickAccessComponent } from './quick-access/quick-access.component';
import { UserSettingsService } from '../../_service/user-settings.service';
import { NotePrintExportService } from '../../_service/note-print-export.service';
import { AiSessionService } from '../../_service/ai-session.service';
import { SemanticSearchResult, SemanticSearchService } from '../../_service/semantic-search.service';
import { FilterLogic, PrivateTagIndex } from './logic/filter.logic';
import { ScopedTagsService } from '../../_service/scoped-tags.service';
import { TAG_FILTER_SCOPE, TAG_SCOPE, getTagScope, isPrivateTag, normalizeTagFilterScope } from '../../_util/tag-scope.util';
import { SortingLogic } from './logic/sorting.logic';
import { BulkActionsLogic, ExportSortOrder } from './logic/bulk-actions.logic';
import { SyncStateLogic, SyncStatus, SyncState, ConnectivityDisplay, resolveConnectivityDisplay } from './logic/sync-state.logic';
import { TemplateLogic } from './logic/template.logic';
import { MenuStateLogic } from './logic/menu-state.logic';
import { NavigationLogic } from './logic/navigation.logic';
import { UIStateLogic } from './logic/ui-state.logic';
import { OutsideClickGuard } from './logic/outside-click-guard';
import { ImportPreviewRow, NoteJsonImportLogic } from './logic/note-json-import.logic';
import { ImportPreviewDialogComponent } from './import-preview-dialog/import-preview-dialog.component';

type CardSort = {
  project_id: number;
  sort_order: number;
};

type HomeUiState = {
  view?: string;
  calendarMonth?: string;
  calendarVisibility?: 'calendar' | 'all';
};

type HumanReadableFilterItem = {
  label: string;
  value: string;
};

const HOME_UI_STATE_STORAGE_KEY = 'notesapp.home.uiState.v1';

@Component({
  selector: 'app-home',
  imports: [
    EnvBadgesComponent,
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    TieredMenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    AlertComponent,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
    SplitButtonModule,
    SliderModule,
    SplitterModule,
    PopoverModule,
    GlobalDialogComponent,
    BulkActionDialogComponent,
    ImportPreviewDialogComponent,
    HeaderComponent,
    ProjectCardsComponent,
    TagCardsComponent,
    ListComponent,
    CalendarViewComponent,
    TaskListComponent,
    QuickAccessComponent,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('pdfContent', { read: ViewContainerRef, static: true }) pdfContent!: ViewContainerRef;
  @ViewChild('DesktopSearchField') DesktopSearchField!: ElementRef<HTMLInputElement>;
  @ViewChild('menu') menu!: Menu;
  @ViewChild('userMenu') userMenu!: Menu;
  @ViewChild('syncLogContainer') syncLogContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('zoomTrigger', { read: ElementRef }) zoomTriggerRef?: ElementRef<HTMLElement>;
  @ViewChild('zoomPanel', { read: ElementRef }) zoomPanelRef?: ElementRef<HTMLElement>;
  @ViewChild('importJsonFileInput') importJsonFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bottomStack') bottomStackRef?: ElementRef<HTMLElement>;
  private bottomStackResizeObserver?: ResizeObserver;
  private readonly ngZone = inject(NgZone);
  /** Height of the fixed bottom stack (List page navigation + footer), so the list can end above it. */
  bottomStackHeight = 0;
  /** The List's page navigation bar (desktop / iPad), shown in the bottom stack; see ListComponent.desktopPagerChange. */
  listPager: TemplateRef<unknown> | null = null;

  get showListPager(): boolean {
    return !!this.listPager && this.selectedView === 'List';
  }
  /** Only populated while `selectedView === 'List'` (the panel is behind an `@if`). The mobile
   * header's Sort button targets this directly — sorting is inherently a List-view concept, same
   * as the desktop table's own clickable column headers. */
  @ViewChild(ListComponent) listComponentRef?: ListComponent;

  // Inject AI session service for opening dialog from menu
  private readonly aiSessionService = inject(AiSessionService);

  /** While a menu is open, a click outside it only closes the menu. */
  private readonly outsideClickGuard = new OutsideClickGuard();

  @HostListener('document:keydown.escape') onEsc() {
    if (this.showZoomPanel) {
      this.showZoomPanel = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isQaSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    const path = (ev.composedPath?.() || []) as EventTarget[];
    const clickedTrigger = this.zoomTriggerRef?.nativeElement ? path.includes(this.zoomTriggerRef.nativeElement) : false;
    const clickedInsidePanel = this.zoomPanelRef?.nativeElement ? path.includes(this.zoomPanelRef.nativeElement) : false;
    
    if (!clickedTrigger && !clickedInsidePanel && this.showZoomPanel) {
      this.showZoomPanel = false;
    }
  }

  allNotes: Note[] = []; // renamed from "notes" 18.Oct.25
  filteredNotes: Note[] = [];

  // Quick-access side panel state
  quickAccessCollapsed = false;
  quickAccessSizes: number[] = [18, 82];
  isQaSmallScreen = false;
  private readonly qaCollapsedKey = 'quick-access-panel-collapsed';
  private readonly qaSizesKey = 'quick-access-panel-sizes';
  private lastQuickAccessSizes: number[] = [18, 82];
  availableTags: Tag[] = [];
  /** tasks/frontend-tagging-phase3.md — every tag Home can filter/display by: the user's own tag list
   * (their private tags) plus the Space tags of every Space they can access. Never another Space's. */
  filterTagCatalog: Tag[] = [];
  private readonly spaceTagsBySpace = new Map<string, Tag[]>();
  /** The current user's private tag ids per note (null until/unless the server provides them). */
  privateTagIndex: PrivateTagIndex = null;
  /** False while the server lacks the bulk private-assignment endpoint — see ScopedTagsService. */
  privateTagFilterAvailable = false;
  private readonly scopedTags = inject(ScopedTagsService);
  private readonly noteSizes = inject(NoteSizeService);
  /** tasks/space_ux.md — every Space the current user has at least View access to. */
  mySpaces: Space[] = [];
  /** IDs of notes the user has marked as favorite (user-settings), for the "Favorites" saved-search
   * shortcut (FilterParams.FavoritesOnly) — kept in sync in the userSettings$ subscription below. */
  private favoriteNoteIds: ReadonlySet<string> = new Set();
  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  filter: FilterParams = {};
  projects: Project[] = [];
  card_sort: CardSort[] = [];
  statusValues: string[] = [];
  types: string[] = [];
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  //selectedProjectId: number | null = null; // selected project ID for export PDF ??
  loadingCounter = 0; // initially count of loading objects in progress
  isOnline: boolean = true; // Tracks online/offline status from EnvironmentService
  // Whether the server actually responded to the last sync attempt — distinct from
  // isOnline, which only reflects the browser's network interface. See SyncStateLogic.
  serverReachable: boolean = true;
  get connectivity(): ConnectivityDisplay {
    return resolveConnectivityDisplay(this.isOnline, this.serverReachable);
  }
  MobileFilterMenuIsVisible: boolean = false;
  MobileAppMenuIsVisible: boolean = false;
  selectedStatus: string = '';
  selectedView: string = 'List';
  calendarMonthState: string | null = null; // yyyy-mm for Calendar view
  calendarVisibilityState: 'calendar' | 'all' = 'calendar';
  templateDialogVisible = false;
  templates: Note[] = [];
  templatesLoading = false;
  templateLoadError = '';
  templateMenuItems: MenuItem[] = [];
  homeZoomScale = 1;
  systemZoomFactor = 100;
  showZoomPanel = false;
  zoomPanelStyle: any = {};
  // Dialog state
  private _showSyncDialog = false;
  isSyncing = false;
  isSelectMode: boolean = false;
  /** False until the notes have been read from local storage (the Tasks view says "Loading tasks…"). */
  notesLoaded = false;
  /** The view was picked in this browser tab (session state or the View menu): a user-settings
   * refresh (e.g. a stale `homeView` from the server) must not switch it to another view. */
  private viewChosenThisSession = false;
  selectedNoteIds: string[] = []; // notes selected in select mode    ?? Unclear if this is needed here!!!!
  private previousViewBeforeSelectMode: string | null = null;
  bulkActions: BulkActionConfig[] = [];
  bulkActionMenuItems: MenuItem[] = [];
  bulkDialogVisible = false;
  currentBulkAction: BulkActionConfig | null = null;
  // Status + messages
  syncStatus: SyncStatus = {
    couchdb: 'offline',
    indexeddb: 'ok',
    lastSynced: null,
  };

  messageLog: string[] = [];

  private sub?: Subscription;

  layoutService: LayoutService = inject(LayoutService);
  selectedProject: Project | undefined;
  isDarkMode = computed(() => this.layoutService.appState().darkMode);
  pageMenuItems: MenuItem[] = [];
  filterMenuItems: MenuItem[] = [];
  userMenuItems: MenuItem[] = [];
  globalStatusMessage: string = 'Please wait...';
  semanticResults: SemanticSearchResult[] = [];
  semanticSearchLoading = false;
  semanticSearchError = '';
  filterInfoDialogVisible = false;
  filterInfoJsonDialogVisible = false;
  appInfoDialogVisible = false;
  readonly envInfo = getEnvironmentInfo();
  appVersionLoading = false;
  appVersionError = '';
  appVersionInfo: AppVersionInfo | null = null;

  // Computed property to check if any filter is applied
  get isFiltered(): boolean {
    return this.hasActiveFilter(this.filter);
  }

  get currentFilterDisplay(): string {
    if (!this.hasActiveFilter(this.filter)) {
      return 'none';
    }

    return JSON.stringify(this.buildPrintableFilter(this.filter), null, 2);
  }

  get clearFilterButtonTitle(): string {
    return `Clear search and reset all filters\n\nCurrently active filter: ${this.currentFilterDisplay}`;
  }

  openFilterInfoDialog(): void {
    this.filterInfoDialogVisible = true;
  }

  openFilterInfoJsonDialog(): void {
    this.filterInfoJsonDialogVisible = true;
  }

  get currentFilterHumanReadable(): HumanReadableFilterItem[] {
    return this.buildHumanReadableFilter(this.filter);
  }

  /** The active filters for the List's printout (printed after the table). The same array is returned
   * until the wording changes, so the (OnPush) list isn't re-rendered on every change-detection pass. */
  get printFilterSummary(): HumanReadableFilterItem[] {
    const rows = this.isFiltered ? this.buildHumanReadableFilter(this.filter) : [];
    const key = JSON.stringify(rows);
    if (key !== this.printFilterSummaryKey) {
      this.printFilterSummaryKey = key;
      this.printFilterSummaryRows = rows;
    }
    return this.printFilterSummaryRows;
  }
  private printFilterSummaryKey = '[]';
  private printFilterSummaryRows: HumanReadableFilterItem[] = [];

  // Get the count of displayed notes
  get displayedNotesCount(): number {
    return this.filteredNotes.length;
  }

  // Get the filter status message
  get filterStatusMessage(): string {
    const status = this.isFiltered ? 'Filtered' : 'Unfiltered';
    return `${status} | ${this.displayedNotesCount} record${this.displayedNotesCount !== 1 ? 's' : ''}`;
  }

  // Observable you can bind in the template (shows newest first)
  synchLog$: Observable<string[]>;
  syncDialogPinned = false;
  syncDialogAllowClose = false;

  get showSyncDialog(): boolean {
    return this._showSyncDialog;
  }

  set showSyncDialog(value: boolean) {
    if (!value && this.syncDialogPinned && !this.syncDialogAllowClose) {
      this._showSyncDialog = true;
      return;
    }
    this._showSyncDialog = value;
  }

  constructor(
    public auth: AuthService,
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private filterService: FilterService,
    private userSettings: UserSettingsService,
    private notePrintExportService: NotePrintExportService,
    private environmentService: EnvironmentService,
    private titleService: Title,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private gDS: GlobalDialogService,
    private logs: LogService,
    private syncCoordinator: SyncCoordinatorService,
    private remoteChanges: RemoteChangesService,
    private semanticSearchService: SemanticSearchService,
    private appVersionService: AppVersionService,
    private appUpdateService: AppUpdateService,
    public dataTarget: DataTargetService,
    private spaceDirectory: SpaceDirectoryService,

  ) {
    this.synchLog$ = this.logs.entries$('synch');
  }

  private destroy$ = new Subject<void>();
  private logAutoScrollInitialized = false;
  private syncStatusTimer: ReturnType<typeof setInterval> | null = null;
  // Static (not instance) so the fingerprint/sorted-notes cache survives
  // HomeComponent being destroyed and recreated on every navigation (there's
  // no RouteReuseStrategy) — without this, returning to Home always recomputes
  // from scratch even when nothing actually changed since you left.
  private static lastDataFingerprint: string = '';
  private static cachedSortedNotes: Note[] = [];
  private semanticSearchRequestToken = 0;

  // ─── Quick-access side panel ──────────────────────────────────────────────

  loadQuickAccessState(): void {
    this.isQaSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;
    try {
      const collapsed = localStorage.getItem(this.qaCollapsedKey);
      this.quickAccessCollapsed = collapsed === 'true';
      const savedSizes = localStorage.getItem(this.qaSizesKey);
      if (savedSizes) {
        const parsed = JSON.parse(savedSizes);
        if (Array.isArray(parsed) && parsed.length === 2) {
          this.quickAccessSizes = parsed;
          this.lastQuickAccessSizes = parsed;
        }
      }
    } catch {
      // ignore
    }
  }

  onQuickAccessResize(event: { sizes: (string | number)[] }): void {
    if (!Array.isArray(event.sizes) || event.sizes.length !== 2) {
      return;
    }
    this.quickAccessSizes = event.sizes.map((s) =>
      typeof s === 'string' ? parseFloat(s) : s,
    );
    this.lastQuickAccessSizes = [...this.quickAccessSizes];
    try {
      localStorage.setItem(this.qaSizesKey, JSON.stringify(this.quickAccessSizes));
    } catch {
      // ignore
    }
  }

  collapseQuickAccess(): void {
    this.lastQuickAccessSizes = [...this.quickAccessSizes];
    this.quickAccessCollapsed = true;
    try {
      localStorage.setItem(this.qaCollapsedKey, 'true');
    } catch {
      // ignore
    }
  }

  expandQuickAccess(): void {
    this.quickAccessCollapsed = false;
    if (this.lastQuickAccessSizes.length === 2) {
      this.quickAccessSizes = [...this.lastQuickAccessSizes];
    }
    try {
      localStorage.setItem(this.qaCollapsedKey, 'false');
    } catch {
      // ignore
    }
  }

  // ─────────────────────────────────────────────────────────────────────────

  private getDefaultFilter(): FilterParams {
    return {
      SearchString: '',
      SearchType: 'Title, Keywords & Tags',
      SemanticTopK: 10,
      Type: '',
      ProjectName: '',
      Status: '(All)',
      TagIds: [],
      TagScope: TAG_FILTER_SCOPE.ANY,
      ModifiedBetween: { start: null, end: null },
      CreatedBetween: { start: null, end: null },
      DateFilterField: 'modified',
      DateFilterMode: 'days',
      DateSinceDays: null,
      DateSinceDate: null,
    };
  }

  private hasRestorableFilter(candidate: unknown): candidate is FilterParams {
    if (!candidate || typeof candidate !== 'object') {
      return false;
    }

    const filterCandidate = candidate as FilterParams;
    const filterKeys: Array<keyof FilterParams> = [
      'SearchString',
      'SearchType',
      'SemanticTopK',
      'Type',
      'ProjectName',
      'Status',
      'TagIds',
      'TagScope',
      'FavoritesOnly',
      'ModifiedBetween',
      'CreatedBetween',
      'DateFilterField',
      'DateFilterMode',
      'DateSinceDays',
      'DateSinceDate',
    ];

    return filterKeys.some((key) => key in filterCandidate);
  }

  private cloneFilter(filter: FilterParams | undefined | null): FilterParams {
    const source = filter ?? {};
    return {
      ...source,
      TagIds: Array.isArray(source.TagIds) ? [...source.TagIds] : [],
      TagScope: normalizeTagFilterScope(source.TagScope),
      SpaceIds: Array.isArray(source.SpaceIds) ? [...source.SpaceIds] : [],
      ModifiedBetween: source.ModifiedBetween
        ? {
            start: source.ModifiedBetween.start ? new Date(source.ModifiedBetween.start) : null,
            end: source.ModifiedBetween.end ? new Date(source.ModifiedBetween.end) : null,
          }
        : { start: null, end: null },
      CreatedBetween: source.CreatedBetween
        ? {
            start: source.CreatedBetween.start ? new Date(source.CreatedBetween.start) : null,
            end: source.CreatedBetween.end ? new Date(source.CreatedBetween.end) : null,
          }
        : { start: null, end: null },
      DateFilterField: source.DateFilterField === 'created' ? 'created' : 'modified',
      DateFilterMode: source.DateFilterMode === 'date' ? 'date' : 'days',
      DateSinceDays:
        typeof source.DateSinceDays === 'number' && Number.isFinite(source.DateSinceDays) && source.DateSinceDays > 0
          ? Math.floor(source.DateSinceDays)
          : null,
      DateSinceDate: source.DateSinceDate ? new Date(source.DateSinceDate) : null,
    };
  }

  private hasActiveFilter(filter: FilterParams | undefined | null): boolean {
    const candidate = filter ?? {};
    const normalize = (value?: string | null): string => (value ?? '').trim();
    const hasDate = (value?: { start: Date | null; end: Date | null } | null): boolean => {
      const start = value?.start instanceof Date && !Number.isNaN(value.start.getTime()) ? value.start : null;
      const end = value?.end instanceof Date && !Number.isNaN(value.end.getTime()) ? value.end : null;
      return !!start || !!end;
    };

    const hasDateSinceDays =
      typeof candidate.DateSinceDays === 'number' && Number.isFinite(candidate.DateSinceDays) && candidate.DateSinceDays > 0;
    const hasDateSinceDate =
      candidate.DateSinceDate instanceof Date && !Number.isNaN(candidate.DateSinceDate.getTime());

    return !!(
      normalize(candidate.SearchString).length > 0 ||
      normalize(candidate.Type).length > 0 ||
      normalize(candidate.ProjectName).length > 0 ||
      (Array.isArray(candidate.TagIds) && candidate.TagIds.some((id) => typeof id === 'string' && id.trim().length > 0)) ||
      (Array.isArray(candidate.ExcludeTagIds) &&
        candidate.ExcludeTagIds.some((id) => typeof id === 'string' && id.trim().length > 0)) ||
      (Array.isArray(candidate.SpaceIds) && candidate.SpaceIds.some((id) => typeof id === 'string' && id.trim().length > 0)) ||
      !!candidate.FavoritesOnly ||
      (normalize(candidate.Status).length > 0 && normalize(candidate.Status) !== '(All)') ||
      hasDateSinceDays ||
      hasDateSinceDate ||
      hasDate(candidate.ModifiedBetween) ||
      hasDate(candidate.CreatedBetween)
    );
  }

  private buildPrintableFilter(filter: FilterParams | undefined | null): Record<string, unknown> {
    const candidate = filter ?? {};
    const normalize = (value?: string | null): string => (value ?? '').trim();
    const formatDate = (value?: Date | null): string | null => {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return null;
      }
      return value.toISOString();
    };
    const tagLabelById = new Map(this.filterTagCatalog.map((tag) => [tag._id, this.tagFilterLabel(tag)]));
    const describeIds = (ids: string[] | undefined, lookup: Map<string, string>) =>
      (Array.isArray(ids) ? ids : [])
        .filter((id) => typeof id === 'string' && id.trim().length > 0)
        .map((id) => ({
          id,
          label: lookup.get(id) ?? id,
        }));

    return {
      SearchString: normalize(candidate.SearchString),
      SearchType: normalize(candidate.SearchType) || 'Title, Keywords & Tags',
      SemanticTopK: Number.isFinite(candidate.SemanticTopK) ? Number(candidate.SemanticTopK) : null,
      IncludeTags: describeIds(candidate.TagIds, tagLabelById),
      TagIncludeMode: candidate.TagIncludeMode === 'OR' ? 'OR' : 'AND',
      ExcludeTags: describeIds(candidate.ExcludeTagIds, tagLabelById),
      TagScope: normalizeTagFilterScope(candidate.TagScope),
      Spaces: (Array.isArray(candidate.SpaceIds) ? candidate.SpaceIds : [])
        .filter((id) => typeof id === 'string' && id.trim().length > 0)
        .map((id) => ({ id, label: this.spaceDirectory.getSpaceName(id) })),
      FavoritesOnly: !!candidate.FavoritesOnly,
      Type: normalize(candidate.Type),
      ProjectName: normalize(candidate.ProjectName),
      Status: normalize(candidate.Status) || '(All)',
      DateFilter: {
        field: candidate.DateFilterField === 'created' ? 'Creation Date' : 'Modified Date',
        mode: candidate.DateFilterMode === 'date' ? 'Since Date' : 'Within Days',
        sinceDays:
          typeof candidate.DateSinceDays === 'number' && Number.isFinite(candidate.DateSinceDays) && candidate.DateSinceDays > 0
            ? Math.floor(candidate.DateSinceDays)
            : null,
        sinceDate: formatDate(candidate.DateSinceDate ?? null),
      },
      ModifiedBetween: {
        start: formatDate(candidate.ModifiedBetween?.start ?? null),
        end: formatDate(candidate.ModifiedBetween?.end ?? null),
      },
      CreatedBetween: {
        start: formatDate(candidate.CreatedBetween?.start ?? null),
        end: formatDate(candidate.CreatedBetween?.end ?? null),
      },
    };
  }

  private buildHumanReadableFilter(filter: FilterParams | undefined | null): HumanReadableFilterItem[] {
    const candidate = filter ?? {};
    const normalize = (value?: string | null): string => (value ?? '').trim();
    const rows: HumanReadableFilterItem[] = [];

    const includeTagIds = Array.isArray(candidate.TagIds)
      ? candidate.TagIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    const excludeTagIds = Array.isArray(candidate.ExcludeTagIds)
      ? candidate.ExcludeTagIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];

    const tagLabelById = new Map(this.filterTagCatalog.map((tag) => [tag._id, this.tagFilterLabel(tag)]));

    const namesFromIds = (ids: string[], lookup: Map<string, string>) =>
      ids.map((id) => lookup.get(id) || id).filter((name) => !!name);

    const formatDate = (value?: Date | null): string => {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return '';
      }
      return value.toLocaleDateString('en-CA');
    };

    const modifiedFrom = formatDate(candidate.ModifiedBetween?.start ?? null);
    const modifiedTo = formatDate(candidate.ModifiedBetween?.end ?? null);
    const createdFrom = formatDate(candidate.CreatedBetween?.start ?? null);
    const createdTo = formatDate(candidate.CreatedBetween?.end ?? null);
    const dateFieldLabel = candidate.DateFilterField === 'created' ? 'Created' : 'Modified';
    const sinceDays =
      typeof candidate.DateSinceDays === 'number' && Number.isFinite(candidate.DateSinceDays) && candidate.DateSinceDays > 0
        ? Math.floor(candidate.DateSinceDays)
        : null;
    const sinceDate = formatDate(candidate.DateSinceDate ?? null);
    const searchType = normalize(candidate.SearchType) || 'Title, Keywords & Tags';
    const status = normalize(candidate.Status);

    const searchString = normalize(candidate.SearchString);
    if (searchString) {
      rows.push({ label: 'Search Text', value: searchString });
      rows.push({ label: 'Search In', value: searchType });
      if (searchType.toLowerCase() === 'semantic' && Number.isFinite(candidate.SemanticTopK)) {
        rows.push({ label: 'Semantic Top K', value: String(Number(candidate.SemanticTopK)) });
      }
    }

    const typeValue = normalize(candidate.Type);
    if (typeValue) {
      rows.push({ label: 'Type', value: typeValue });
    }

    const projectName = normalize(candidate.ProjectName);
    if (projectName) {
      rows.push({ label: 'Project', value: projectName });
    }

    if (status && status !== '(All)') {
      rows.push({ label: 'Status', value: status });
    }

    if (includeTagIds.length > 0) {
      rows.push({
        label: 'Include Tags',
        value: namesFromIds(includeTagIds, tagLabelById).join(', '),
      });
      if (includeTagIds.length > 1) {
        rows.push({
          label: 'Include Tag Logic',
          value: candidate.TagIncludeMode === 'OR' ? 'Match any selected tag (OR)' : 'Match all selected tags (AND)',
        });
      }
    }

    if (excludeTagIds.length > 0) {
      rows.push({
        label: 'Exclude Tags',
        value: namesFromIds(excludeTagIds, tagLabelById).join(', '),
      });
    }

    const tagScope = normalizeTagFilterScope(candidate.TagScope);
    if ((includeTagIds.length > 0 || excludeTagIds.length > 0) && tagScope !== TAG_FILTER_SCOPE.ANY) {
      rows.push({ label: 'Tag Scope', value: tagScope === TAG_FILTER_SCOPE.PRIVATE ? 'Private tags only' : 'Space tags only' });
    }

    const spaceIds = Array.isArray(candidate.SpaceIds)
      ? candidate.SpaceIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    if (spaceIds.length > 0) {
      rows.push({
        label: 'Spaces',
        value: spaceIds.map((id) => this.spaceDirectory.getSpaceName(id)).join(', '),
      });
    }

    if (candidate.FavoritesOnly) {
      rows.push({ label: 'Favorites', value: 'Only favorite notes' });
    }

    if (sinceDays) {
      rows.push({
        label: 'Date Filter',
        value: `${dateFieldLabel} within ${sinceDays} ${sinceDays === 1 ? 'day' : 'days'}`,
      });
    } else if (sinceDate) {
      rows.push({
        label: 'Date Filter',
        value: `${dateFieldLabel} since ${sinceDate}`,
      });
    } else if (modifiedFrom || modifiedTo) {
      rows.push({
        label: 'Modified Date',
        value: modifiedFrom && modifiedTo ? `${modifiedFrom} to ${modifiedTo}` : modifiedFrom ? `From ${modifiedFrom}` : `Until ${modifiedTo}`,
      });
    } else if (createdFrom || createdTo) {
      rows.push({
        label: 'Created Date',
        value: createdFrom && createdTo ? `${createdFrom} to ${createdTo}` : createdFrom ? `From ${createdFrom}` : `Until ${createdTo}`,
      });
    }

    return rows.filter((row) => row.value.trim().length > 0);
  }

  private restoreFilter(navState: Record<string, unknown>): FilterParams {
    const navigationFilter = navState['filter'];
    const persistedFilter = this.filterService.getFilterParams();
    const restored = this.hasRestorableFilter(navigationFilter)
      ? navigationFilter
      : this.hasRestorableFilter(persistedFilter)
        ? persistedFilter
        : this.getDefaultFilter();

    const filter = this.cloneFilter(restored);
    if (!filter.SearchType || filter.SearchType.trim() === '') {
      filter.SearchType = 'Title, Keywords & Tags';
    }
    if (!filter.SemanticTopK || filter.SemanticTopK < 1) {
      filter.SemanticTopK = 10;
    }
    return filter;
  }

  private persistActiveFilter(): void {
    this.filterService.setFilterParams(this.cloneFilter(this.filter));
  }

  async ngOnInit() {
    this.outsideClickGuard.attach();
    try {
      console.log('HomeComponent - in ngOnInit()');
      const navState = this.router.getCurrentNavigation()?.extras?.state ?? (history.state || {});
      const requestedViewFromNav = navState['view'];
      // Router navigation `state` doesn't survive a window.open() into a brand-new
      // browsing context (e.g. the header's "New Calendar window" button) — a URL
      // query param is the only way to request a starting view across windows.
      const requestedViewFromQuery = this.route.snapshot.queryParamMap.get('view');
      const requestedHomeState = navState['homeState'] as HomeUiState | undefined;
      const persistedHomeState = this.readPersistedHomeUiState();
      const requestedView =
        (typeof requestedViewFromNav === 'string' && requestedViewFromNav.trim() !== ''
          ? requestedViewFromNav
          : undefined) ??
        (requestedViewFromQuery && requestedViewFromQuery.trim() !== '' ? requestedViewFromQuery : undefined) ??
        (typeof requestedHomeState?.view === 'string' && requestedHomeState.view.trim() !== ''
          ? requestedHomeState.view
          : undefined);

      this.calendarMonthState =
        (typeof requestedHomeState?.calendarMonth === 'string' && requestedHomeState.calendarMonth.trim() !== ''
          ? requestedHomeState.calendarMonth
          : null) ??
        (typeof persistedHomeState.calendarMonth === 'string' && persistedHomeState.calendarMonth.trim() !== ''
          ? persistedHomeState.calendarMonth
          : null);

      this.calendarVisibilityState =
        requestedHomeState?.calendarVisibility === 'all' || requestedHomeState?.calendarVisibility === 'calendar'
          ? requestedHomeState.calendarVisibility
          : persistedHomeState.calendarVisibility === 'all' || persistedHomeState.calendarVisibility === 'calendar'
            ? persistedHomeState.calendarVisibility
            : 'calendar';

      this.updateTemplateMenuItems();
      this.setTitle('Home');
      this.alertService.clear();
      this.loadQuickAccessState();
      void this.loadAppVersionInfo();
      // Render immediately from local caches; refresh remote-dependent data in background.
      this.availableTags = Array.isArray(this.dataService.allTags)
        ? [...this.dataService.allTags]
        : [];
      this.rebuildFilterTagCatalog();
      void this.loadTagCatalogInBackground();
      void this.loadPrivateTagIndex();
      void this.refreshUserSettingsInBackground();

      // tasks/space_ux.md — Space filter/column/mobile-card. Render immediately from whatever's
      // cached (offline-safe), then refresh in the background like availableTags above.
      this.mySpaces = this.spaceDirectory.spaces;
      this.spaceDirectory.spaces$.pipe(takeUntil(this.destroy$)).subscribe((spaces) => {
        this.mySpaces = spaces;
        void this.loadSpaceTagCatalogs(spaces);
        this.cdr.markForCheck();
      });
      this.spaceDirectory.refresh();

      // 2️⃣ Restore or initialize filter
      this.filter = this.restoreFilter(navState as Record<string, unknown>);
      this.persistActiveFilter();
      // 3️⃣ Watch user settings
      this.userSettings.userSettings$.pipe(takeUntil(this.destroy$)).
        subscribe((settings) => {
        if (!settings) return;
        console.log('UserSettings loaded:', settings);
        if (settings.primaryColor) {
          this.layoutService.appState.update((state: any) => ({
            ...state,
            primary: settings.primaryColor,
          }));
        }
        // Restore the last active view
        if (!requestedView && !this.viewChosenThisSession && settings.homeView) {
          this.selectedView = this.normalizeView(settings.homeView);
          this.persistHomeUiState();
          this.updateHomeTitle();
        }
        // For the "Favorites" saved-search shortcut (FilterParams.FavoritesOnly) — re-filter
        // immediately if it's the active filter, since a note may have just been (un)favorited.
        this.favoriteNoteIds = new Set(
          Array.isArray(settings.favoriteNoteIds)
            ? settings.favoriteNoteIds.filter((id): id is string => typeof id === 'string')
            : [],
        );
        if (this.filter.FavoritesOnly) {
          this.applyFilters();
        }
      });
      if (typeof requestedView === 'string' && requestedView.trim() !== '') {
        this.selectedView = this.normalizeView(requestedView);
      } else if (typeof persistedHomeState.view === 'string' && persistedHomeState.view.trim() !== '') {
        this.selectedView = this.normalizeView(persistedHomeState.view);
        this.viewChosenThisSession = true;
      }
      this.persistHomeUiState();
      this.updateHomeTitle();
      this.userSettings.systemZoomFactor$.pipe(takeUntil(this.destroy$)).subscribe((factor) => {
        const safeFactor = typeof factor === 'number' && !Number.isNaN(factor) ? factor : 100;
        console.log('HomeComponent: system zoom factor =', safeFactor);
        this.systemZoomFactor = safeFactor;
        this.homeZoomScale = safeFactor / 100;
      });

      // 4️⃣ Network status - subscribe to online/offline changes
      this.environmentService.online$
        .pipe(takeUntil(this.destroy$))
        .subscribe((status) => {
          this.isOnline = status;
          this.refreshSyncStatus();
          this.updateSyncStatusMessage();
          this.cdr.markForCheck();
        });

      // Server reachability - distinct from browser online/offline (see SyncStateLogic):
      // the browser can report "online" while the app's server is unreachable.
      this.syncCoordinator.serverReachable$
        .pipe(takeUntil(this.destroy$))
        .subscribe((reachable) => {
          this.serverReachable = reachable;
          this.refreshSyncStatus();
          this.updateSyncStatusMessage();
          this.cdr.markForCheck();
        });

      // 5️⃣ Init projects / statuses
      this.projects = this.dataService.getAllProjects();
      this.statusValues = this.dataService.getAllStatuses();
      this.types = this.dataService.getAllNoteTypes();
      this.initializeBulkActions();

      const lastSync = this.syncCoordinator.getLastSyncCompleted();
      if (lastSync) {
        this.syncStatus = { ...this.syncStatus, lastSynced: lastSync };
      }
      this.syncCoordinator
        .onSyncCompleted()
        .pipe(takeUntil(this.destroy$))
        .subscribe((time) => {
          this.syncStatus = { ...this.syncStatus, lastSynced: time };
          this.updateSyncStatusMessage();
          void this.dataService.refreshLocalData();
          this.refreshTagDataAfterSync();
        });
      this.updateSyncStatusMessage();
      if (!this.syncStatusTimer) {
        this.syncStatusTimer = setInterval(() => this.updateSyncStatusMessage(), 60_000);
      }

      this.dataService.dataReloaded$
        .pipe(
          filter((stamp): stamp is Date => stamp instanceof Date),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.refreshFromMemory());

      // 6️⃣ Wait for data load and fetch notes
      this.dataService.dataLoaded$
        .pipe(
          distinctUntilChanged(), // only fire on false→true transition, not on every sync reload
          filter(Boolean),
          switchMap(() => this.dataService.getAllNotes()),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (response: ApiResponse<Note[]>) => {
            if (response.status === 'success' && response.data) {
              const notes = response.data;
              console.log('✅ Notes loaded:', notes.length, 'notes');

              // Skip the full re-sort when the data is identical to what the
              // last (possibly destroyed) HomeComponent instance already
              // computed — common when returning via the Close button.
              const fingerprint = this.createDataFingerprint(notes);
              if (fingerprint === HomeComponent.lastDataFingerprint && HomeComponent.cachedSortedNotes.length === notes.length) {
                console.log('[ngOnInit] Data unchanged since last load, reusing cached sort');
                this.allNotes = HomeComponent.cachedSortedNotes;
              } else {
                this.allNotes = notes.sort(
                  (a: Note, b: Note) =>
                    new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
                );
                HomeComponent.lastDataFingerprint = fingerprint;
                HomeComponent.cachedSortedNotes = this.allNotes;
              }

              this.assignSortOrderToProjects(this.projects, this.allNotes,
                this.card_sort);
              this.sortProjectsByCardSort();
              this.sortNotesByCardSort();
              this.applyFilters();
              this.notesLoaded = true;
              this.cdr.detectChanges();
            } else {
              const msg = response.error?.message ||
                'Failed to load notes.';
              console.warn('⚠️ Load failed:', msg);
              this.alertService?.warn?.(msg, { life: 4000 });
              this.notesLoaded = true; // nothing more is coming: don't keep saying "Loading"
            }
          },
          error: (err) => {
            console.error('Failed to load notes:', err);
            this.notesLoaded = true;
            this.alertService?.error?.(
              err?.message || 'Could not load local notes.',
              { life: 5000 },
            );
          },
          complete: () => {
            // no-op
          },
        });

      // 7️⃣ Handle refresh
      if (history.state?.refresh) this.getItems();

      console.log('ngOnInit complete');
    } catch (err: any) {
      console.error('Unexpected initialization error:', err);
      this.gDS.showGlobalDialog(
        'Critical Error',
        'Initialization failed',
        err?.message || 'Unexpected app crash.',
        DialogType.Error,
      );
    }
  }

  ngAfterViewInit(): void {
    this.trackBottomStackHeight();
    if (this.logAutoScrollInitialized) return;
    this.logAutoScrollInitialized = true;
    this.synchLog$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.scrollSyncLogToBottom();
    });
  }

  /** Keeps bottomStackHeight current (the page navigation bar comes and goes, the footer can grow). */
  private trackBottomStackHeight(): void {
    const stack = this.bottomStackRef?.nativeElement;
    if (!stack || typeof ResizeObserver === 'undefined') return;
    this.bottomStackResizeObserver = new ResizeObserver(() => {
      const height = Math.ceil(stack.getBoundingClientRect().height);
      if (height !== this.bottomStackHeight) this.ngZone.run(() => (this.bottomStackHeight = height));
    });
    this.bottomStackResizeObserver.observe(stack);
  }

  ngOnDestroy(): void {
    this.bottomStackResizeObserver?.disconnect();
    this.outsideClickGuard.detach();
    this.destroy$.next();
    this.destroy$.complete();
    UIStateLogic.updateBodySelectModeClass(false);
    if (this.syncStatusTimer) {
      clearInterval(this.syncStatusTimer);
      this.syncStatusTimer = null;
    }
  }


  // ##############################################################################
  // DATA LOADING FUNCTIONS
  // ##############################################################################

  get isLoading(): boolean {
    return this.loadingCounter > 0;
  }

  getItems(): void {
    this.loadingCounter++; // Start loading
    console.log('*** in getItems() ***');

    this.dataService.getAllNotes().subscribe({
      next: (response) => {
        if (response.status === 'success' && response.data) {
          this.allNotes = response.data.sort((a, b) => {
            const dateA = new Date(a.created_on);
            const dateB = new Date(b.created_on);
            return dateB.getTime() - dateA.getTime(); // DESC by created_on
          });

          // Perform your existing post-processing steps
          this.assignSortOrderToProjects(this.projects, this.allNotes, 
            this.card_sort);
          this.sortProjectsByCardSort();
          this.sortNotesByCardSort();
          this.applyFilters();
          this.notesLoaded = true;
        } else {
          const msg = response.error?.message || 
            'Failed to load notes (unknown error).';
          console.warn('⚠️ getItems():', msg);
          this.alertService?.warn?.(msg, { life: 4000 });
        }

        this.loadingCounter--; // Always stop loading, even on handled error
      },

      error: (err) => {
        console.error('❌ getItems(): unexpected error:', err);
        this.alertService?.error?.('An unexpected error occurred while loading notes.', 
            { life: 5000 });
        this.loadingCounter--; // Ensure loading flag decrements
      },
    });
  }

  // ##############################################################################
  // SYNCH-RELATED ROUTINES
  // ##############################################################################

  /** Open the dialog and refresh current sync status */
  openSyncDialog(): void {
    console.log('HomeComponent: openSyncDialog()');
    this.syncDialogPinned = true;
    this.syncDialogAllowClose = false;
    this.showSyncDialog = true;
    this.refreshSyncStatus();
    // this.dataService.fetchRemoteDbInfoAndLogActive();
    // this.dataService.fetchOfflineDbInfoAndLogActive();
    // setTimeout(() => this.scrollSyncLogToBottom(), 0);
  }

  getDbInfos(): void {
    this.refreshSyncStatus();
    this.dataService.fetchRemoteDbInfoAndLogActive();
    this.dataService.fetchOfflineDbInfoAndLogActive();
  }


  /** Close the dialog */
  closeSyncDialog(): void {
    this.syncDialogPinned = false;
    this.syncDialogAllowClose = true;
    this.showSyncDialog = false;
  }

  onSyncDialogHide(): void {
    if (this.syncDialogAllowClose) {
      this.syncDialogAllowClose = false;
      this.showSyncDialog = false;
      return;
    }
    if (this.syncDialogPinned) {
      setTimeout(() => {
        this.showSyncDialog = true;
      }, 0);
      return;
    }
    this.showSyncDialog = true;
  }

  /** Pull latest statuses from your services */
  refreshSyncStatus(): void {
    // Preserve lastSynced — SyncStateLogic.refreshSyncStatus() only recomputes
    // the couchdb/indexeddb status from current connectivity, it doesn't know
    // about the last successful sync time tracked separately via onSyncCompleted().
    this.syncStatus = {
      ...SyncStateLogic.refreshSyncStatus(this.isOnline, this.serverReachable),
      lastSynced: this.syncStatus.lastSynced,
    };
  }

  /** While syncing, show transient 'syncing' states without 
   * losing offline/error info permanently */
  private setTransientStates(state: SyncState): void {
    this.syncStatus = SyncStateLogic.setTransientStates(this.syncStatus, state);
  }

  private updateSyncStatusMessage(): void {
    // The "OFFLINE" / "Server not responding" state itself is conveyed by the footer's
    // red/green icon and its mouse-over tooltip (connectivity.label) — kept out of this
    // visible text so the footer bar doesn't grow every time connectivity drops.
    const lastSynced = this.syncStatus.lastSynced;
    if (!lastSynced) {
      this.globalStatusMessage = `Last sync: never`;
      return;
    }

    const diffMs = Date.now() - lastSynced.getTime();
    const minutes = Math.max(0, Math.floor(diffMs / 60000));

    if (minutes < 1) {
      this.globalStatusMessage = `Last sync: just now`;
      return;
    }
    if (minutes < 60) {
      this.globalStatusMessage = `Last sync: ${minutes}m ago`;
      return;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      this.globalStatusMessage = `Last sync: ${hours}h ago`;
      return;
    }
    const days = Math.floor(hours / 24);
    this.globalStatusMessage = `Last sync: ${days}d ago`;
  }

  trackByIndex(i: number): number {
    return i;
  }

  /** Notes with local changes not yet confirmed pushed to the server (new/updated/deleted). */
  get unsyncedNotesCount(): number {
    return this.allNotes.reduce(
      (count, note) => count + (note.syncStatus && note.syncStatus !== 'synced' ? 1 : 0),
      0,
    );
  }

  /** Message shown in the footer's connectivity popover — makes the user aware of how
   * many local changes are still waiting to reach the server while offline. */
  get connectivityPopoverMessage(): string {
    const count = this.unsyncedNotesCount;
    const pending = count > 0
      ? `${count} note${count === 1 ? '' : 's'} with unsynced local change${count === 1 ? '' : 's'}.`
      : 'No unsynced local changes.';

    if (this.connectivity.level === 'offline') {
      return `NotesApp is offline. ${pending}`;
    }
    if (this.connectivity.level === 'server-unreachable') {
      return `Server is not responding. ${pending}`;
    }
    return `Online — everything is in sync. ${pending}`;
  }

  // src/app/_components/home/home.component.ts

  synchNow(): void {
  this.isSyncing = true;
  this.setTransientStates('syncing');
  this.logs.append('synch', '[MANUAL] incremental sync requested');

  this.syncCoordinator
    .forceIncrementalSync('manual')
    .then(() => this.refreshData())
    .finally(() => (this.isSyncing = false));
}


async forceBaseline() {
  await this.syncCoordinator.runManualBaseline();
}

  async forceBaselineConfirmed(): Promise<void> {
    const ok = confirm(
      'Force baseline sync? This clears IndexedDB and re-downloads all notes.',
    );
    if (!ok) return;
    await this.forceBaseline();
  }


// synchNow(): void {
//   this.isSyncing = true;

//   this.dataService
//     .syncAllChanges('manual')
//     .catch((err) => console.error('Manual sync failed', err))
//     .finally(() => {
//       this.isSyncing = false;
//     });
// }

//   async synchNow(): Promise<void> {
//     console.log('home: in synchNow()');
//     if (this.isSyncing) return;

//     this.isSyncing = true;
//     this.setTransientStates('syncing');
//     this.loadingCounter++;

//     try {
//       await this.dataService.syncAllChanges();
//       console.log('Data synchronized successfully');
//       this.getItems(); // Refresh items after synchronization
//       this.alertService.success('Synchronization successful');

//       this.syncStatus = SyncStateLogic.setSuccessState(this.syncStatus);
//     } catch (err: any) {
//       this.logs.append('synch', 'Sync failed!');
//       this.syncStatus = SyncStateLogic.setErrorState(this.syncStatus);
//       this.gDS.showGlobalDialog(
//         'Synchronization Error',
//         'Failed to synchronize data',
//         err?.message || 'An unknown error occurred.',
//         DialogType.Error,
//       );
//       this.logs.append('synch', err?.message || 'An unknown error occurred.');
//     } finally {
//       this.loadingCounter--;
//       this.isSyncing = false;
//     }
//   }

  clearOfflineStorage(): void {
    const ok = confirm(
        'Are you sure you want to clear all offline storage? This action cannot be undone.');
    if (!ok) return;
    console.log('Clearing offline storage...');
    this.dataService.clearOfflineStorage();
  }

  clearSynchLog(): void {
    this.logs.clear('synch');
  }

  resetSyncCursor(): void {
    const ok = confirm(
      'Reset sync cursor to 0? The next incremental sync will scan from the start.',
    );
    if (!ok) return;
    this.remoteChanges.resetAllLastSeq();
  }

  private scrollSyncLogToBottom(): void {
    if (!this.syncLogContainer?.nativeElement) {
      return;
    }
    const element = this.syncLogContainer.nativeElement;
    const schedule =
      typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 0);
    schedule(() => {
      element.scrollTop = element.scrollHeight;
    });
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

  // called from header component
  onSearchChanged(query: string) {
    //console.log("SearchString: ",searchText);
    this.filter.SearchString = query;
    this.persistActiveFilter();
    this.applyFilters(); // Apply filters when filter changes
  }

  // from header component
  onFilterChanged(filter: any) {
    this.filter = this.cloneFilter(filter);
    console.log('HomeComponent: onFilterChanged(): filter = ', this.filter);
    this.persistActiveFilter();
    this.applyFilters();
  }

  onTagClicked(tag: Tag) {
    this.filter.TagIds = [tag._id];
    this.persistActiveFilter();
    this.applyFilters();
    if (this.selectedView !== 'Tags') {
      this.changeView('List');
      this.userSettings.setLocal({ homeView: 'List' });
    }
  }

  /** Mobile header's Sort button. `field` is one of the sortBy keys ListComponent understands
   * ('Modified' | 'Created' | 'ReferenceDate' | 'Title' | 'Space'); re-selecting the same field toggles its
   * order — that logic already lives in ListComponent.sortData(), so it's just forwarded here. */
  onSortRequested(field: string): void {
    this.listComponentRef?.sortData(field);
  }

  onQuickAccessRelatedTagSelected(tagId: string): void {
    if (typeof tagId !== 'string' || !tagId.trim()) {
      return;
    }

    const normalizedTagId = tagId.trim();
    const nextFilter = this.cloneFilter(this.filter);
    const nextTagIds = new Set(Array.isArray(nextFilter.TagIds) ? nextFilter.TagIds : []);
    nextTagIds.add(normalizedTagId);

    nextFilter.TagIds = Array.from(nextTagIds);
    nextFilter.TagIncludeMode = 'AND';
    nextFilter.ExcludeTagIds = Array.isArray(nextFilter.ExcludeTagIds)
      ? nextFilter.ExcludeTagIds.filter((id) => id !== normalizedTagId)
      : [];

    this.filter = nextFilter;
    this.persistActiveFilter();
    this.applyFilters();
  }

  onQuickAccessRelatedTagRemoved(tagId: string): void {
    if (typeof tagId !== 'string' || !tagId.trim()) {
      return;
    }

    const normalizedTagId = tagId.trim();
    const nextFilter = this.cloneFilter(this.filter);
    const nextTagIds = Array.isArray(nextFilter.TagIds)
      ? nextFilter.TagIds.filter((id) => id !== normalizedTagId)
      : [];

    nextFilter.TagIds = nextTagIds;
    nextFilter.TagIncludeMode = 'AND';

    this.filter = nextFilter;
    this.persistActiveFilter();
    this.applyFilters();
  }

  onQuickAccessRelatedTagExcluded(tagId: string): void {
    if (typeof tagId !== 'string' || !tagId.trim()) {
      return;
    }

    const normalizedTagId = tagId.trim();
    const nextFilter = this.cloneFilter(this.filter);
    const nextExcluded = new Set(Array.isArray(nextFilter.ExcludeTagIds) ? nextFilter.ExcludeTagIds : []);
    nextExcluded.add(normalizedTagId);

    nextFilter.ExcludeTagIds = Array.from(nextExcluded);
    nextFilter.TagIds = Array.isArray(nextFilter.TagIds)
      ? nextFilter.TagIds.filter((id) => id !== normalizedTagId)
      : [];
    nextFilter.TagIncludeMode = 'AND';

    this.filter = nextFilter;
    this.persistActiveFilter();
    this.applyFilters();
  }

  toggleDarkMode() {
    UIStateLogic.toggleDarkMode(this.layoutService);
  }

  setCurrentView(view: string) {
    view = this.normalizeView(view);
    this.viewChosenThisSession = true;
    this.userSettings.setLocal({ homeView: view });
    this.selectedView = view;
    if (view === 'Calendar' && !this.calendarMonthState) {
      this.calendarMonthState = this.toMonthKey(new Date());
    }
    this.persistHomeUiState();
    this.updateHomeTitle();
  }

  /** The Category-based "Cards" view was retired along with Categories; anyone whose
   * settings/session/URL still names it falls back to List instead of a blank screen. */
  private normalizeView(view: string): string {
    return view === 'CardView' ? 'List' : view;
  }

  onCalendarMonthChanged(monthKey: string): void {
    if (typeof monthKey !== 'string' || monthKey.trim() === '') {
      return;
    }
    this.calendarMonthState = monthKey;
    this.persistHomeUiState();
  }

  onCalendarVisibilityChanged(mode: 'calendar' | 'all'): void {
    this.calendarVisibilityState = mode === 'all' ? 'all' : 'calendar';
    this.persistHomeUiState();
  }

  private toMonthKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  private readPersistedHomeUiState(): HomeUiState {
    try {
      const raw = sessionStorage.getItem(HOME_UI_STATE_STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw) as HomeUiState;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private persistHomeUiState(): void {
    try {
      const state: HomeUiState = {
        view: this.selectedView,
        calendarMonth: this.calendarMonthState || undefined,
        calendarVisibility: this.calendarVisibilityState,
      };
      sessionStorage.setItem(HOME_UI_STATE_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage failures
    }
  }

  onPageMenuAction(item: string): void {
    const actions: Record<string, () => void> = {
      New: () => this.createNewNote(),
      NewFromTemplate: () => this.openTemplateDialog(),
      List: () => this.setCurrentView('List'),
      Tags: () => this.setCurrentView('Tags'),
      Tasks: () => {
        // The notes' Select mode doesn't carry over: the Tasks view selects tasks, not notes.
        if (this.isSelectMode && this.selectedView !== 'Tasks') this.selectMode();
        this.setCurrentView('Tasks');
      },
      Projects: () => this.setCurrentView('Projects'),
      Calendar: () => this.setCurrentView('Calendar'),
      Refresh: () => this.refreshApp(),
      Select: () => this.selectMode(),
      DownloadJson: () => this.downloadNotesAsJson(),
      ImportJson: () => this.triggerImportJsonFilePicker(),
      ViewImportLog: () => this.openImportLogDialog(),
      UserSettings: () => this.goToUserSettings(),
      AppInfo: () => this.openAppInfoDialog(),
      // Session-only: flips the in-memory layout state, never touches user-settings/localStorage.
      // The explicit, persisted toggle lives on the User Settings page (app.config.ts /
      // user-settings.component.ts), which calls userSettings.setLocal({ darkMode }) itself.
      ToggleDarkMode: () => this.toggleDarkMode(),
      CheckForUpdate: () => this.appUpdateService.checkForUpdatesNow(),
      Manage: () => this.goToManage(),
      Synchronisation: () => this.openSyncDialog(),
      Trash: () => this.router.navigate(['/trash']),
      AI: () => {
        const filteredNoteIds = this.isFiltered
          ? [...new Set((this.filteredNotes || []).map((note) => String(note?._id ?? '').trim()).filter((id) => !!id))]
          : [];
        this.aiSessionService.openDialog({ filteredNoteIds });
      },
      DebugAI: () => this.router.navigate(['/ai/administration']),
      ReportNotesappErrors: () => this.router.navigate(['/reports/notesapp-errors']),
      ReportAiRequestsLog: () => this.router.navigate(['/reports/ai-requests-log']),
      ReportAiLimits: () => this.router.navigate(['/reports/ai-limits']),
      Logout: () => this.logout(),
    };

    const action = actions[item];
    if (action) {
      action();
    } else {
      console.warn(`Unhandled menu item: ${item}`);
    }
  }

  // refactor filter-menu code
  onSelectFilterMenuItem(selectedItem: string): void {
    this.applyFilters(); // Apply filters when filter changes
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  private updateHomeTitle(): void {
    this.setTitle(`Home (${this.selectedView})`);
  }

  assignSortOrderToProjects(projects: Project[] | undefined, noteItems: Note[], 
        card_sort: CardSort[]) {
    SortingLogic.assignSortOrderToProjects(projects, noteItems, card_sort);
  }

  sortNotesByCardSort(): void {
    this.filteredNotes = SortingLogic.sortNotesByCardSort(this.filteredNotes, this.card_sort);
  }

  sortProjectsByCardSort(): void {
    SortingLogic.sortProjectsByCardSort(this.projects, this.card_sort);
  }

//   getNoteYearPrefix(note: Note): string {
//     return FilterLogic.getNoteYearPrefix(note);
//   }

  getLastModifiedNotes(projectId: number): Note[] {
    return FilterLogic.getLastModifiedNotes(projectId, this.filteredNotes);
  }

  // count the number of filtered notes for a project
  calculateItemCount(projectId: number): number {
    return FilterLogic.calculateItemCount(projectId, this.filteredNotes);
  }

  viewAllNotes(projectId: number): void {
    // this.router.navigate(['/project', projectId]);
  }

  toggleHeaderVisibility(): void {
    this.isHeaderVisible = UIStateLogic.toggleHeaderVisibility(this.isHeaderVisible);
  }

  clickCardHeader(proj: Project): void {
    NavigationLogic.clickCardHeader(this.router, proj, this.filter);
  }

  clickRow(row: Note) {
    NavigationLogic.clickRow(this.router, row, this.filter);
  }

  isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return element.offsetParent !== null && style.visibility !== 'hidden' 
        && style.display !== 'none';
  }

  // ##############################################################################
  // NAVIGATION FUNCTIONS
  // ##############################################################################

  changeView(view: string): void {
    console.log('changeView to:', view);
    this.selectedView = view;
    this.updateHomeTitle();
  }

  navigateToEdit(row: Note) {
    NavigationLogic.navigateToEdit(this.router, row, this.filter);
  }

  logout(): void {
    NavigationLogic.logout(this.router, this.auth);
    // Auth state is now cleared — switch off the just-logged-out account's
    // IndexedDB database and reset in-memory/sync state so nothing from
    // that session lingers if another account logs in on this device.
    this.syncCoordinator.onAccountChanged();
    void this.dataService.initialize();
  }

  goToManage(): void {
    NavigationLogic.goToManage(this.router, this.filter);
  }
  goToUserSettings(): void {
    NavigationLogic.goToUserSettings(this.router, this.filter);
  }

  openAppInfoDialog(): void {
    this.appInfoDialogVisible = true;
    if (!this.appVersionInfo && !this.appVersionLoading) {
      void this.loadAppVersionInfo();
    }
  }

  refreshAppInfoDialog(): void {
    void this.loadAppVersionInfo(true);
  }

  get appVersionDisplayBuiltAt(): string {
    const builtAt = this.appVersionInfo?.builtAtUtc;
    if (!builtAt) {
      return 'Not available';
    }
    const parsed = new Date(builtAt);
    return Number.isNaN(parsed.getTime()) ? builtAt : parsed.toLocaleString();
  }

  private async loadAppVersionInfo(forceRefresh = false): Promise<void> {
    this.appVersionLoading = true;
    this.appVersionError = '';
    try {
      this.appVersionInfo = await this.appVersionService.getVersionInfo(forceRefresh);
    } catch (err: any) {
      this.appVersionError = err?.message || 'Unable to load application version info.';
    } finally {
      this.appVersionLoading = false;
    }
  }

  // ##############################################################################
  // MISC LOGIC FUNCTIONS
  // ##############################################################################

  downloadNotesAsJson(): void {
    this.dataService.getAllNotes().subscribe({
      next: (data) => {
        const jsonStr = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const now = new Date();
        const timestamp = now.getFullYear().toString() +
          (now.getMonth() + 1).toString().padStart(2, '0') +
          now.getDate().toString().padStart(2, '0') + '-' +
          now.getHours().toString().padStart(2, '0') +
          now.getMinutes().toString().padStart(2, '0') +
          now.getSeconds().toString().padStart(2, '0');
        const a = document.createElement('a');
        a.href = url;
        a.download = `NotesAppData_Backup_${timestamp}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading notes as JSON:', err);
        this.alertService?.error?.('Failed to download notes as JSON.', { life: 5000 });
      },
    });
  }

  // ------------------------------------------------------------------------
  // Import JSON Note(s)... — reads a file produced by
  // NotePrintExportService.exportNotesToJson(), lets the user resolve any
  // _id conflicts against the current account's own notes in one dialog,
  // then saves each accepted note locally (syncStatus 'new'/'updated' — the
  // normal push cycle picks it up shortly after, same as any local edit).
  // ------------------------------------------------------------------------

  importPreviewDialogVisible = false;
  importPreviewRows: ImportPreviewRow[] = [];
  importFileErrors: string[] = [];

  // Diagnostic log for the "Import JSON Note(s)..." feature — every warning
  // and error encountered while importing the currently/most-recently
  // selected file, viewable on demand via "View Import Log" so a failed or
  // partial import isn't a silent black box.
  importLogDialogVisible = false;
  importLogEntries: string[] = [];

  private logImport(message: string): void {
    const timestamp = new Date().toLocaleTimeString();
    this.importLogEntries = [...this.importLogEntries, `[${timestamp}] ${message}`];
  }

  openImportLogDialog(): void {
    this.importLogDialogVisible = true;
  }

  triggerImportJsonFilePicker(): void {
    this.importJsonFileInput?.nativeElement?.click();
  }

  async onImportJsonFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Allow re-selecting the same file next time.
    input.value = '';
    if (!file) return;

    // Start a fresh log for this import attempt.
    this.importLogEntries = [];
    this.logImport(`Selected file "${file.name}" (${file.size} bytes).`);

    let text: string;
    try {
      text = await file.text();
    } catch (err: any) {
      this.logImport(`Could not read the selected file: ${err?.message ?? String(err)}`);
      this.alertService.error('Could not read the selected file.', { life: 5000 });
      return;
    }

    const { records, fileErrors } = NoteJsonImportLogic.parseImportFile(text);
    if (fileErrors.length) {
      fileErrors.forEach((e) => this.logImport(`File error: ${e}`));
      this.importFileErrors = fileErrors;
      this.importPreviewRows = [];
      this.importPreviewDialogVisible = true;
      return;
    }

    let existingIdSet: Set<string>;
    try {
      const existing = await firstValueFrom(this.dataService.getAllNotes());
      existingIdSet = new Set((existing.data ?? []).map((n) => n._id));
    } catch {
      existingIdSet = new Set(this.allNotes.map((n) => n._id));
    }

    this.importFileErrors = [];
    this.importPreviewRows = NoteJsonImportLogic.buildPreviewRows(records, existingIdSet);
    this.logImport(
      `Parsed ${this.importPreviewRows.length} note(s): ` +
        `${this.importPreviewRows.filter((r) => r.status === 'new').length} new, ` +
        `${this.importPreviewRows.filter((r) => r.status === 'conflict').length} already exist, ` +
        `${this.importPreviewRows.filter((r) => r.status === 'invalid').length} invalid.`,
    );
    this.importPreviewRows
      .filter((r) => r.status === 'invalid')
      .forEach((r) => this.logImport(`${r.key}: invalid — ${r.errors.join(' ')}`));
    this.importPreviewDialogVisible = true;
  }

  onImportPreviewCancel(): void {
    this.importPreviewDialogVisible = false;
  }

  async onImportPreviewConfirm(rows: ImportPreviewRow[]): Promise<void> {
    this.importPreviewDialogVisible = false;

    const toImport = rows.filter((r) => r.decision === 'import' || r.decision === 'overwrite');
    const skipped = rows.length - toImport.length;
    this.logImport(`Confirmed import: ${toImport.length} accepted, ${skipped} skipped.`);

    // Online: prefer the bulk import endpoint so the summary reflects the
    // real backend outcome (obsolete/unknown fields ignored, validation
    // failures) instead of only the local save. Falls back to the offline
    // path below on any network failure — this must never block the dialog.
    if (this.dataService.isOnline) {
      const handledOnline = await this.importRowsViaBulkEndpoint(toImport, skipped);
      if (handledOnline) return;
    }

    await this.importRowsOffline(toImport, skipped);
  }

  /** Builds note payloads for the accepted rows and imports them in one
   * request via POST /api/notes/import/bulk. Returns false (instead of
   * throwing) on any failure so the caller can fall back to the
   * offline-first local save — this must never leave the user's import
   * un-actioned just because the network call didn't work. */
  private async importRowsViaBulkEndpoint(toImport: ImportPreviewRow[], skipped: number): Promise<boolean> {
    if (!toImport.length) {
      this.logImport('Nothing to import online (no accepted rows).');
      this.alertService.warn(skipped ? `Nothing was imported (${skipped} skipped).` : 'Nothing was imported.', { life: 5000 });
      return true;
    }

    let notes: Note[];
    try {
      this.logImport(`Importing ${toImport.length} note(s) online via bulk import endpoint...`);
      notes = [];
      for (const row of toImport) {
        const existingResp = row.decision === 'overwrite' ? await firstValueFrom(this.dataService.getNote(row.record._id)) : null;
        const existing = existingResp?.data ?? undefined;
        notes.push(NoteJsonImportLogic.buildNoteFromRecord(row.record, existing));
      }

      const response = await firstValueFrom(this.dataService.importNotesBulk(notes));

      // Save each successfully-imported note locally as synced, so local
      // and server state agree immediately instead of waiting for the next
      // background push cycle to pick it up. Log every per-note warning and
      // error the backend returned, since the summary line only has counts.
      for (const result of response.results ?? []) {
        const id = notes[result.index]?._id ?? `#${result.index}`;
        if (result.status === 'success' && result.data) {
          await this.dataService.saveNoteOffline(result.data, true);
          (result.warnings ?? []).forEach((w) => this.logImport(`${id}: warning — ${w}`));
        } else {
          const details = result.error?.details?.length ? ` (${result.error.details.join(' ')})` : '';
          this.logImport(`${id}: failed — ${result.error?.message ?? 'Unknown error'}${details}`);
        }
      }

      await this.dataService.refreshLocalData();
      this.applyFilters();

      const { imported = 0, warned = 0, failed = 0 } = response.summary ?? {};
      const parts: string[] = [];
      parts.push(`${imported} note${imported === 1 ? '' : 's'} imported successfully`);
      if (warned) parts.push(`${warned} contained obsolete fields that were ignored`);
      if (failed) parts.push(`${failed} failed validation`);
      if (skipped) parts.push(`${skipped} skipped`);
      const summary = parts.join('. ') + '.';
      this.logImport(`Online import done: ${summary}`);

      if (failed && !imported) {
        this.alertService.error(`Import failed: ${summary} See "View Import Log" for details.`, { life: 6000 });
      } else if (failed || warned) {
        this.alertService.warn(`Import complete: ${summary} See "View Import Log" for details.`, { life: 6000 });
      } else {
        this.alertService.success(`Import complete: ${summary}`, { life: 5000 });
      }
      // Don't make the user hunt for the log when something needs attention.
      if (failed || warned) this.openImportLogDialog();
      return true;
    } catch (err: any) {
      const message = err?.message ?? String(err);
      this.logImport(`Online bulk import failed (${message}) — falling back to offline-only import.`);
      console.warn('Bulk import failed; falling back to offline-only import.', err);
      return false;
    }
  }

  /** Offline-first fallback (and the only path when the device is offline):
   * saves each accepted row locally and marks it dirty for the existing
   * background sync push to pick up later. The summary here can only
   * reflect the local outcome, since the backend hasn't seen these notes yet. */
  private async importRowsOffline(toImport: ImportPreviewRow[], skipped: number): Promise<void> {
    let imported = 0;
    let overwritten = 0;
    let failed = 0;

    if (toImport.length) {
      this.logImport(`Saving ${toImport.length} note(s) locally (offline import) — will sync to the server later.`);
    }

    for (const row of toImport) {
      try {
        const existingResp = row.decision === 'overwrite' ? await firstValueFrom(this.dataService.getNote(row.record._id)) : null;
        const existing = existingResp?.data ?? undefined;
        const note = NoteJsonImportLogic.buildNoteFromRecord(row.record, existing);
        await this.dataService.saveNoteOffline(note, false);
        if (row.decision === 'overwrite') overwritten++;
        else imported++;
      } catch (err: any) {
        console.error('Failed to import note', row.record?._id, err);
        this.logImport(`${row.key}: failed — ${err?.message ?? String(err)}`);
        failed++;
      }
    }

    if (imported || overwritten) {
      await this.dataService.refreshLocalData();
      this.applyFilters();
    }

    const parts: string[] = [];
    if (imported) parts.push(`${imported} imported`);
    if (overwritten) parts.push(`${overwritten} overwritten`);
    if (skipped) parts.push(`${skipped} skipped`);
    if (failed) parts.push(`${failed} failed`);
    const summary = parts.length ? parts.join(', ') + '.' : 'Nothing was imported.';
    this.logImport(`Offline import done: ${summary}`);

    if (failed && !imported && !overwritten) {
      this.alertService.error(`Import failed: ${summary} See "View Import Log" for details.`, { life: 6000 });
    } else if (failed) {
      this.alertService.warn(`Import completed with errors: ${summary} See "View Import Log" for details.`, { life: 6000 });
    } else {
      this.alertService.success(`Import complete: ${summary}`, { life: 5000 });
    }
    // Don't make the user hunt for the log when something needs attention.
    if (failed) this.openImportLogDialog();
  }

createNewNote(): void {
  this.dataService.createNote().subscribe(
    (resp) => {
      const id = resp?.data?._id;
      if (id) {
        this.router.navigateByUrl(`/edit/${encodeURIComponent(id)}`, {
          state: { filter: this.filter },
        });
      } else {
        console.warn('Create note did not return an _id', resp);
      }
    },
    (err: any) => {
      console.error('Error creating note:', err);
    }
  );
}
  openTemplateDialog(): void {
    this.templateDialogVisible = true;
    // Use cached templates for instant display
    this.templates = this.dataService.getTemplatesFromCache();
    this.templatesLoading = false;
    this.templateLoadError = '';
    this.updateTemplateMenuItems();
  }

  onTemplateMenuOpened(): void {
    // Use cached templates for instant display
    this.templates = this.dataService.getTemplatesFromCache();
    this.templatesLoading = false;
    this.templateLoadError = '';
    this.updateTemplateMenuItems();
  }

  loadTemplates(): void {
    // Legacy method - now just uses cache
    // Kept for backward compatibility
    this.templates = this.dataService.getTemplatesFromCache();
    this.templatesLoading = false;
    this.templateLoadError = '';
    this.updateTemplateMenuItems();
  }

  updateTemplateMenuItems(): void {
    this.templateMenuItems = TemplateLogic.buildTemplateMenuItems(
      this.templatesLoading,
      this.templateLoadError,
      this.templates,
      (template) => this.createNoteFromTemplate(template),
    );
  }
createNoteFromTemplate(template: Note): void {
  this.dataService.createNoteFromTemplate(template).subscribe(
    (resp) => {
      if (resp?.status === 'success') {
        const id = resp?.data?._id;
        if (id) {
          this.templateDialogVisible = false;
          this.router.navigateByUrl(`/edit/${encodeURIComponent(id)}`, {
            state: { filter: this.filter },
          });
          return;
        }
        console.warn('Create from template did not return an _id', resp);
      }
      const msg =
        (resp && 'error' in resp && resp.error?.message)
          ? resp.error.message
          : 'Failed to create note from template.';
      this.gDS.showGlobalDialog(
        'Create Note Failed',
        'Unable to create note from template',
        msg,
        DialogType.Error,
      );
    },
    (err: any) => {
      console.error('Error creating note from template:', err);
      const msg = err?.message || 'Failed to create note from template.';
      this.gDS.showGlobalDialog(
        'Create Note Failed',
        'Unable to create note from template',
        msg,
        DialogType.Error,
      );
    }
  );
}

  stripHtml(html: string): string {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    return tempElement.textContent || tempElement.innerText || '';
  }

  private deltaToPlainText(delta: Delta | null | undefined): string {
    return FilterLogic.deltaToPlainText(delta);
  }


  applyFilters(): void {
  if (this.isSemanticSearchActive) {
    this.runSemanticSearch();
    return;
  }

  this.applyKeywordStyleFilters();
}

  private applyKeywordStyleFilters(): void {
  this.semanticSearchLoading = false;
  this.semanticSearchError = '';
  this.semanticResults = [];

  const normalizedFilter = this.normalizeFilterForSearch(this.filter);

  this.filteredNotes = FilterLogic.applyFilters(
    this.allNotes,
    normalizedFilter,
    this.projects,
    this.filterTagCatalog,
    this.favoriteNoteIds,
    this.privateTagIndex,
  );
}

  private runSemanticSearch(): void {
    const queryText = (this.filter.SearchString ?? '').trim();
    if (!queryText) {
      this.semanticSearchLoading = false;
      this.semanticSearchError = '';
      this.semanticResults = [];
      this.applyKeywordStyleFilters();
      return;
    }

    const topK = this.filter.SemanticTopK && this.filter.SemanticTopK > 0 ? this.filter.SemanticTopK : 10;
    const token = ++this.semanticSearchRequestToken;
    this.semanticSearchLoading = true;
    this.semanticSearchError = '';
    this.semanticResults = [];
    this.filteredNotes = [];

    this.semanticSearchService.searchNotes({ query_text: queryText, top_k: topK, prefer_recent: true }).subscribe({
      next: (response) => {
        if (token !== this.semanticSearchRequestToken) {
          return;
        }
        this.semanticResults = Array.isArray(response?.data?.results) ? response.data!.results! : [];
        this.filteredNotes = this.mapSemanticResultsToNotes(this.semanticResults, queryText);
        this.semanticSearchLoading = false;
      },
      error: (err) => {
        if (token !== this.semanticSearchRequestToken) {
          return;
        }
        this.semanticResults = [];
        this.semanticSearchLoading = false;
        this.semanticSearchError = err?.error?.message || err?.message || 'Semantic search failed.';
      },
    });
  }

  get isSemanticSearchActive(): boolean {
    return (this.filter.SearchType ?? '').trim().toLowerCase() === 'semantic';
  }

  private mapSemanticResultsToNotes(results: SemanticSearchResult[], queryText: string): Note[] {
    if (!Array.isArray(results) || results.length === 0) {
      return this.findLexicalSemanticFallbackNotes(queryText);
    }

    const notesById = new Map<string, Note>();
    for (const note of this.allNotes) {
      const rawId = String(note._id ?? '').trim();
      if (!rawId) {
        continue;
      }
      const normalized = this.normalizeSemanticNoteId(rawId);
      notesById.set(rawId, note);
      notesById.set(normalized, note);
      if (!rawId.startsWith('note:')) {
        notesById.set(`note:${rawId}`, note);
      }
      if (rawId.startsWith('note:')) {
        notesById.set(rawId.slice('note:'.length), note);
      }
    }

    const mapped: Note[] = [];
    for (const row of results) {
      const rawId = String(row.note_id ?? row.id ?? '').trim();
      if (!rawId) {
        continue;
      }
      const normalizedId = this.normalizeSemanticNoteId(rawId);
      const note = notesById.get(rawId) ?? notesById.get(normalizedId);
      if (note) {
        mapped.push(note);
      }
    }

    const seen = new Set(mapped.map((note) => String(note._id)));
    const lexicalFallback = this.findLexicalSemanticFallbackNotes(queryText).filter((note) => !seen.has(String(note._id)));
    return [...mapped, ...lexicalFallback];
  }

  private normalizeSemanticNoteId(value: string): string {
    const trimmed = String(value ?? '').trim().toLowerCase();
    return trimmed.startsWith('note:') ? trimmed.slice('note:'.length) : trimmed;
  }

  private findLexicalSemanticFallbackNotes(queryText: string): Note[] {
    const query = String(queryText ?? '').trim();
    if (!query) {
      return [];
    }

    // Use the same filter engine as normal search mode so lexical matches are consistent.
    const lexicalFilter = this.normalizeFilterForSearch({
      ...this.filter,
      SearchType: 'Title, Keywords & Tags',
      SearchString: query,
    });

    return FilterLogic.applyFilters(
      this.allNotes,
      lexicalFilter,
      this.projects,
      this.filterTagCatalog,
      this.favoriteNoteIds,
      this.privateTagIndex,
    );
  }

  private normalizeFilterForSearch(input: FilterParams): FilterParams {
    return {
      ...input,
      TagIds: Array.isArray(input.TagIds) && input.TagIds.length > 0 ? input.TagIds : undefined,
      ProjectName: input.ProjectName && input.ProjectName.trim() !== '' ? input.ProjectName : undefined,
      Type: input.Type && input.Type.trim() !== '' ? input.Type : undefined,
      Status: input.Status && input.Status.trim() !== '' ? input.Status : undefined,
      SearchString: input.SearchString && input.SearchString.trim() !== '' ? input.SearchString : '',
    };
  }

  private async loadTagCatalog(): Promise<void> {
    try {
      const response = await firstValueFrom(this.dataService.getAllTags());
      if (response.status === 'success' && response.data) {
        this.availableTags = response.data;
        this.rebuildFilterTagCatalog();
        this.applyFilters();
      }
    } catch (err) {
      console.warn('Failed to load tags for search matching:', err);
    }
  }

  /** At most this often, a completed sync also refreshes tag data (the sync itself runs every ~30s).
   * Kept long on purpose: the server's tag-list endpoints currently read the whole database. */
  private static readonly TAG_REFRESH_MIN_INTERVAL_MS = 10 * 60_000;
  private lastTagRefreshAt = Date.now(); // ngOnInit has just loaded everything

  /**
   * Tags don't travel with the note sync: Space tags are their own documents in each Space, and the
   * user's private tag assignments live in their own store. After a sync, refresh both (rate-limited)
   * so tags created/renamed/recoloured by other Space members, and private tags assigned on another
   * device, show up without leaving Home.
   */
  private refreshTagDataAfterSync(): void {
    if (!this.dataService.isOnline || document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - this.lastTagRefreshAt < HomeComponent.TAG_REFRESH_MIN_INTERVAL_MS) return;
    this.lastTagRefreshAt = now;
    void this.loadTagCatalogInBackground();
    this.spaceTagsBySpace.clear(); // reload every Space's tags (cached lists render meanwhile)
    void this.loadSpaceTagCatalogs(this.mySpaces);
    void this.loadPrivateTagIndex();
  }

  private async loadTagCatalogInBackground(): Promise<void> {
    await this.loadTagCatalog();
    this.cdr.markForCheck();
  }

  /** Space tags of every accessible SHARED Space — one request per Space, not per note (the user's own
   * PRIVATE Space shares their tag database, already covered by availableTags). Cached lists render
   * first, so filtering by Space tags keeps working offline. */
  private async loadSpaceTagCatalogs(spaces: Space[]): Promise<void> {
    const shared = spaces.filter((space) => space.type === 'SHARED');
    const accessible = new Set(shared.map((space) => space.id));
    for (const spaceId of [...this.spaceTagsBySpace.keys()]) {
      if (!accessible.has(spaceId)) this.spaceTagsBySpace.delete(spaceId); // access lost: drop its tags
    }
    const toLoad = shared.filter((space) => !this.spaceTagsBySpace.has(space.id));
    for (const space of toLoad) {
      this.spaceTagsBySpace.set(space.id, this.scopedTags.getCachedSpaceTags(space.id));
    }
    this.rebuildFilterTagCatalog();
    await Promise.all(
      toLoad.map(async (space) => {
        const tags = await firstValueFrom(this.scopedTags.loadSpaceTags(space.id));
        if (this.spaceTagsBySpace.has(space.id)) this.spaceTagsBySpace.set(space.id, tags);
      }),
    );
    this.rebuildFilterTagCatalog();
    this.applyFilters();
    this.cdr.markForCheck();
  }

  private rebuildFilterTagCatalog(): void {
    const byId = new Map<string, Tag>();
    for (const tag of this.availableTags) byId.set(tag._id, tag);
    for (const tags of this.spaceTagsBySpace.values()) {
      for (const tag of tags) byId.set(tag._id, tag);
    }
    this.filterTagCatalog = [...byId.values()];
  }

  /** A tag as named in filter summaries: private tags and Space tags stay distinguishable. */
  private tagFilterLabel(tag: Tag): string {
    return getTagScope(tag) === TAG_SCOPE.PRIVATE
      ? `${tag.name} (private)`
      : `${tag.name} (${this.spaceDirectory.getSpaceName(tag.spaceId ?? undefined)})`;
  }

  /**
   * List view: one tag added to / removed from one note (its Tags cell, or the phone's "Edit tags"
   * dialog). Never changes the note's Modified date:
   * - a Space tag is a tag-only save of Note.Tags (IndexedDB first, then the normal sync push — works
   *   offline), which keeps last_modified (DataService.saveNoteTags);
   * - a private tag goes through the private-tag-assignment API (online only); the Note isn't touched.
   */
  async onNoteTagChange({ note, tag, op }: NoteTagChange): Promise<void> {
    const current = this.allNotes.find((n) => n._id === note._id) ?? note;
    if (isPrivateTag(tag)) {
      await this.changeNotePrivateTag(current, tag, op);
      return;
    }
    const ids = (current.Tags ?? []).map((t) => (typeof t === 'string' ? t : String(t?._id ?? ''))).filter(Boolean);
    const next = op === 'add' ? (ids.includes(tag._id) ? ids : [...ids, tag._id]) : ids.filter((id) => id !== tag._id);
    if (next.length === ids.length && next.every((id, i) => id === ids[i])) return;
    const updated: Note = { ...current, Tags: next };
    try {
      await this.dataService.saveNoteTags(updated);
    } catch (err: any) {
      this.alertService.error(err?.message || 'The tags could not be saved.');
      return;
    }
    this.allNotes = this.allNotes.map((n) => (n._id === updated._id ? updated : n));
    this.applyFilters();
    this.cdr.markForCheck();
    if (this.dataService.isOnline) {
      void this.syncCoordinator.forceIncrementalSync('auto');
    }
  }

  private async changeNotePrivateTag(note: Note, tag: Tag, op: 'add' | 'remove'): Promise<void> {
    if (!this.dataService.isOnline) {
      this.alertService.warn('Private tags can only be changed while online.');
      return;
    }
    const before = this.privateTagIndex?.get(note._id) ?? [];
    const optimistic = op === 'add' ? [...before.filter((id) => id !== tag._id), tag._id] : before.filter((id) => id !== tag._id);
    const setIds = (ids: readonly string[]) => {
      const index = new Map(this.privateTagIndex ?? []);
      index.set(note._id, ids);
      this.privateTagIndex = index;
      this.applyFilters();
      this.cdr.markForCheck();
    };
    setIds(optimistic);
    const resp = await firstValueFrom(
      op === 'add'
        ? this.scopedTags.addPrivateTags(note._id, [tag._id], note.spaceId ?? null)
        : this.scopedTags.removePrivateTags(note._id, [tag._id], note.spaceId ?? null),
    );
    if (resp.status === 'success') {
      setIds(resp.data ?? optimistic);
    } else {
      setIds(before);
      this.alertService.error(resp.error?.message || 'Failed to update private tags.');
    }
  }

  /** A tag just created from the List's "Assign tag…": into the catalog, so it shows and can be offered. */
  onTagCreated(tag: Tag): void {
    if (isPrivateTag(tag)) {
      this.availableTags = [...this.availableTags.filter((t) => t._id !== tag._id), tag];
    } else if (tag.spaceId) {
      this.scopedTags.upsertCachedSpaceTag(tag);
      const tags = this.spaceTagsBySpace.get(tag.spaceId) ?? [];
      this.spaceTagsBySpace.set(tag.spaceId, [...tags.filter((t) => t._id !== tag._id), tag]);
    }
    this.rebuildFilterTagCatalog();
    this.cdr.markForCheck();
  }

  /** All of the user's private tag assignments in one request (see ScopedTagsService.loadPrivateTagIndex). */
  private async loadPrivateTagIndex(): Promise<void> {
    // Render from the offline cache first (private tags in the list/filters right away), then refresh.
    const cached = this.scopedTags.cachedPrivateTagIndex();
    if (cached.available) {
      this.privateTagFilterAvailable = true;
      this.privateTagIndex = cached.byNote;
      this.applyFilters();
    }
    const result = await firstValueFrom(this.scopedTags.loadPrivateTagIndex());
    this.privateTagFilterAvailable = result.available;
    this.privateTagIndex = result.available ? result.byNote : null;
    this.applyFilters();
    this.cdr.markForCheck();
  }

  private async refreshUserSettingsInBackground(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.userSettings.get().pipe(
          catchError((error) => {
            console.warn('Background user settings refresh failed', error);
            return of({ status: 'error' } as { status: 'error'; data?: undefined });
          }),
        ),
      );

      if (response.status === 'success' && response.data) {
        this.userSettings.setLocal(response.data);
      }
    } catch (err) {
      console.warn('Background user settings refresh exception', err);
    }
  }

  refreshApp() {
    this.refreshData();
  }

  private async refreshData(): Promise<void> {
    this.loadingCounter++;
    this.gDS.showGlobalDialog('Please wait', 'Refreshing data...', '', DialogType.Loading);
    try {
      await this.dataService.refreshLocalData();
      const response = await firstValueFrom(this.dataService.getAllNotes());
      if (response.status === 'success' && response.data) {
        const notes = response.data;
        this.allNotes = notes.sort(
          (a: Note, b: Note) =>
            new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
        );
        this.assignSortOrderToProjects(this.projects, this.allNotes, this.card_sort);
        this.sortProjectsByCardSort();
        this.sortNotesByCardSort();
        this.applyFilters();
      } else {
        const msg = response.error?.message || 'Failed to refresh data.';
        this.alertService?.warn?.(msg, { life: 4000 });
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to refresh data.';
      this.alertService?.error?.(msg, { life: 5000 });
    } finally {
      this.loadingCounter--;
      this.gDS.hide();
      this.cdr.detectChanges();
    }
  }

  private async refreshFromMemory(): Promise<void> {
    try {
      const response = await firstValueFrom(this.dataService.getAllNotes());
      if (response.status === 'success' && response.data) {
        const notes = response.data;

        // Create a fingerprint of the data to detect actual changes
        const newFingerprint = this.createDataFingerprint(notes);

        // Only update if data actually changed
        if (newFingerprint === HomeComponent.lastDataFingerprint) {
          console.log('[refreshFromMemory] Data unchanged, skipping redraw');
          return;
        }

        console.log('[refreshFromMemory] Data changed, updating view');
        HomeComponent.lastDataFingerprint = newFingerprint;

        this.allNotes = notes.sort(
          (a: Note, b: Note) =>
            new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
        );
        HomeComponent.cachedSortedNotes = this.allNotes;
        this.assignSortOrderToProjects(this.projects, this.allNotes, this.card_sort);
        this.sortProjectsByCardSort();
        this.sortNotesByCardSort();
        this.applyFilters();
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.error('Failed to refresh data from memory:', err);
    }
  }

  /**
   * Create a fingerprint of notes to detect changes
   */
  private createDataFingerprint(notes: Note[]): string {
    // Create a lightweight fingerprint using counts and key properties
    const noteFingerprint = notes.map(n =>
      `${n._id}:${n._rev}:${n.last_modified}`
    ).join('|');

    return `${notes.length}:${noteFingerprint}`;
  }


  // ##############################################################################
  // SELECT MODE / HANDLING BULK ACTIONS
  // ##############################################################################

  clearSelectedNotes() {
    this.selectedNoteIds = [];
  }

  selectAllFilteredNotes() {
    const uniqueIds = new Set(
      (this.filteredNotes || [])
        .map((note) => note?._id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    );
    this.selectedNoteIds = Array.from(uniqueIds);
  }

  initializeBulkActions() {
    this.bulkActions = MenuStateLogic.initializeBulkActions();
    this.bulkActionMenuItems = MenuStateLogic.buildBulkActionMenuItems(
      this.bulkActions,
      (actionId) => this.onBulkActionSelected(actionId),
    );
  }

  selectMode() {
    console.log('Toggling select mode. Currently:', this.isSelectMode);
    const enabling = !this.isSelectMode;
    this.isSelectMode = enabling;
    this.cdr.detectChanges();
    UIStateLogic.updateBodySelectModeClass(enabling);

    // In the Tasks view, Select mode selects tasks (TaskListComponent has its own selection and actions).

    const viewUpdate = UIStateLogic.getViewForSelectMode(
      enabling,
      this.selectedView,
      this.previousViewBeforeSelectMode,
    );
    this.selectedView = viewUpdate.view;
    this.previousViewBeforeSelectMode = viewUpdate.previousView;
    this.updateHomeTitle();

    if (!enabling) {
      this.selectedNoteIds = [];
    }
  }

  onBulkActionSelected(actionId: string) {
    if (actionId === 'set-favorite') {
      this.addSelectedNotesToFavorites();
      return;
    }
    const action = this.bulkActions.find((a) => a.id === actionId);
    if (!action) {
      return;
    }
    if (actionId === 'add-tags') {
      this.bulkAddTagGroups = this.buildBulkAddTagGroups();
    }
    if (actionId === 'remove-tags') {
      this.bulkRemoveTagGroups = this.buildBulkRemoveTagGroups();
    }
    this.currentBulkAction = action;
    this.bulkDialogVisible = true;
  }

  onBulkActionDialogCancel() {
    this.bulkDialogVisible = false;
    this.currentBulkAction = null;
  }

  /** Bulk "Set as favorite": adds every selected note to the user's Favorites (a user setting —
   * the notes themselves, and so their modified dates, are not touched). Offline-first, same as the
   * View/Edit heart: the local settings are updated first and the server push may follow later. */
  addSelectedNotesToFavorites(): void {
    const selected = this.selectedNoteIds.filter((id) => !!id);
    if (!selected.length) {
      return;
    }
    const current = [...this.favoriteNoteIds]; // kept current from userSettings$
    const currentSet = new Set(current);
    const added = selected.filter((id) => !currentSet.has(id));
    const count = selected.length;
    const noun = count === 1 ? 'note' : 'notes';
    if (added.length) {
      const updated = [...current, ...added];
      this.userSettings.setLocal({ favoriteNoteIds: updated });
      this.userSettings.updateFavorites(updated).subscribe({
        error: (err) => console.warn('Failed to sync favorites to server (kept local change):', err),
      });
    }
    this.alertService.success(
      added.length === count
        ? `Added ${count} ${noun} to Favorites.`
        : `Added ${added.length} of ${count} ${noun} to Favorites (the others already were).`,
    );
    this.clearSelectedNotes();
  }

  get bulkRemovableTags(): Tag[] {
    if (!this.selectedNoteIds.length) {
      return [];
    }

    const selectedIdSet = new Set(this.selectedNoteIds);
    const usedTagIdSet = new Set<string>();

    for (const note of this.allNotes) {
      if (!selectedIdSet.has(note._id)) {
        continue;
      }
      for (const tagRef of note.Tags || []) {
        const tagId = typeof tagRef === 'string' ? tagRef : tagRef?._id;
        if (tagId) {
          usedTagIdSet.add(tagId);
        }
      }
    }

    if (!usedTagIdSet.size) {
      return [];
    }

    const byId = new Map((this.availableTags || []).map((tag) => [tag._id, tag]));

    return Array.from(usedTagIdSet)
      .map((id) => {
        const known = byId.get(id);
        if (known) {
          return known;
        }
        return {
          _id: id,
          type: 'tag',
          name: id,
          tagType: 'user',
          slug: id,
          parentTagId: null,
          isActive: true,
        } as Tag;
      })
      .sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' }));
  }

  async onBulkActionDialogSubmit(request: BulkActionRequest) {
    console.log('Bulk action requested:', request, 'on notes', 
        this.selectedNoteIds);
    this.bulkDialogVisible = false;

    switch (request.action) {
      case 'export':
        await this.handleBulkExport(request.params);
        break;
      case 'print':
        await this.handleBulkPrintExport('print', request.params);
        break;
      case 'set-status':
        await this.handleBulkSetStatus(request.params?.status, 
            request.params?.updateTimestamp);
        break;
      case 'add-tags':
        await this.handleBulkAddTags(request.params?.tagIds);
        break;
      case 'remove-tags':
        await this.handleBulkRemoveTags(request.params?.removeAllTags, request.params?.removeTagIds);
        break;
      case 'move-to-space':
        await this.handleBulkMove(request.params?.destinationSpaceId);
        break;
      case 'delete':
        await this.handleBulkDelete();
        break;
      default:
        this.alertService.warn('This bulk action is not implemented yet.');
        break;
    }

    this.currentBulkAction = null;
  }

  

  private async handleBulkSetStatus(status?: string, updateTimestamp?: boolean) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before applying a bulk status.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!status) {
      this.gDS.showGlobalDialog(
        'Status Required',
        'Choose a status before applying the bulk action.',
        '',
        DialogType.Error,
      );
      return;
    }

    const shouldUpdateTimestamp = updateTimestamp !== false;

    try {
      const resp = await firstValueFrom(
        this.dataService.bulkSetStatus(this.selectedNoteIds, status, 
            shouldUpdateTimestamp),
      );

      if (resp.status === 'success') {
        const updatedCount = this.getUpdatedCountFromBulk(resp.data, 
            this.selectedNoteIds.length);
        const message = resp.data?.message || 
            `Status set to "${status}" for ${updatedCount} note(s).`;
        this.alertService.success(message);
        this.applyBulkStatusLocally(status, shouldUpdateTimestamp);
        this.clearSelectedNotes();
        return;
      }

      const errorMsg = resp.error?.message || 'Bulk status update failed.';
      this.gDS.showGlobalDialog('Bulk Set Status Failed', 
        'Unable to update status.', errorMsg, DialogType.Error);
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error while updating status.';
      this.gDS.showGlobalDialog('Bulk Set Status Failed', 
        'Unable to update status.', errorMsg, DialogType.Error);
    }
  }

  /** tasks/space-move-client.md — Spaces the selected notes can be moved into: active Spaces where the
   * user can write (the server requires edit rights at the destination). */
  get bulkMoveDestinations(): Array<{ id: string; label: string }> {
    return this.mySpaces
      .filter(
        (space) =>
          space.status === 'ACTIVE' &&
          (space.current_user_role === 'OWNER' || space.current_user_role === 'ADMIN' || space.current_user_role === 'EDITOR'),
      )
      .sort((a, b) => (a.type !== b.type ? (a.type === 'PRIVATE' ? -1 : 1) : a.name.localeCompare(b.name)))
      .map((space) => ({ id: space.id, label: space.type === 'PRIVATE' ? 'Private' : space.name }));
  }

  /** Moves the selection with ONE bulk request. Notes already in the destination are simply left out
   * (the server would otherwise reject the whole batch); notes it can't move yet (unsynced changes,
   * never synced) block the move so nothing half-happens. Modified dates are never touched. */
  private async handleBulkMove(destinationSpaceId?: string) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog('Bulk Action Unavailable', 'Select Mode needs to be enabled to run bulk actions.', '', DialogType.Error);
      return;
    }
    if (!this.selectedNoteIds.length || !destinationSpaceId) {
      this.gDS.showGlobalDialog('No Notes Selected', 'Select one or more notes and a destination Space.', '', DialogType.Error);
      return;
    }

    const destinationName = this.bulkMoveDestinations.find((d) => d.id === destinationSpaceId)?.label ?? 'the selected Space';
    const privateSpaceId = this.mySpaces.find((space) => space.type === 'PRIVATE')?.id;
    const selected = new Set(this.selectedNoteIds);
    const notes = this.allNotes.filter((note) => selected.has(note._id));
    const toMove = notes.filter((note) => (note.spaceId || privateSpaceId) !== destinationSpaceId);
    if (!toMove.length) {
      this.alertService.info(`The selected notes are already in ${destinationName}.`);
      return;
    }
    const blocked = toMove.filter((note) => this.dataService.moveBlockedReason(note));
    if (blocked.length) {
      this.gDS.showGlobalDialog(
        'Move Not Possible Yet',
        `${blocked.length} selected note(s) can't be moved yet.`,
        this.dataService.moveBlockedReason(blocked[0]) ?? '',
        DialogType.Error,
      );
      return;
    }

    const resp = await firstValueFrom(this.dataService.bulkMoveNotes(toMove.map((note) => note._id), destinationSpaceId));
    if (resp.status === 'error' || !resp.data) {
      this.gDS.showGlobalDialog('Move Failed', 'No notes were moved.', resp.error?.message || 'Moving the notes failed.', DialogType.Error);
      return;
    }

    const result = resp.data;
    const failedIds = new Set([...result.errors.map((e) => e.note_id), ...result.duplicated]);
    this.applyFilters();
    // The destination's revisions come with the next pull; nudge it rather than wait for the timer.
    void this.syncCoordinator.forceIncrementalSync('auto');

    if (!failedIds.size) {
      this.clearSelectedNotes();
      this.alertService.success(`Moved ${result.moved} note(s) to ${destinationName}.`);
      return;
    }

    // Partial: keep only the notes that didn't move selected, and say exactly what happened.
    this.selectedNoteIds = this.selectedNoteIds.filter((id) => failedIds.has(id));
    const details = [
      ...result.errors.map((e) => `${this.allNotes.find((n) => n._id === e.note_id)?.Title || e.note_id}: ${e.error}`),
      ...result.duplicated.map(
        (id) => `${this.allNotes.find((n) => n._id === id)?.Title || id}: now exists in both Spaces — flagged for cleanup`,
      ),
    ].join('\n');
    this.gDS.showGlobalDialog(
      'Move Partly Completed',
      `Moved ${result.moved} of ${result.requested} note(s) to ${destinationName}. The notes that didn't move are still selected.`,
      details,
      DialogType.Error,
    );
  }

  private async handleBulkDelete() {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog('No Notes Selected', 
        'Select one or more notes before deleting.', '', DialogType.Error);
      return;
    }

    try {
      const resp = await 
        firstValueFrom(this.dataService.bulkDelete(this.selectedNoteIds));

      if (resp.status === 'success') {
        const deletedCount = 
            this.getUpdatedCountFromBulk(resp.data, this.selectedNoteIds.length);
        const message = resp.data?.message || `Moved ${deletedCount} note(s) to Trash.`;
        this.alertService.success(message);
        this.removeNotesLocally(this.selectedNoteIds);
        this.clearSelectedNotes();
        // Refresh template cache in case templates were deleted
        this.dataService.refreshTemplateCache();
        return;
      }

      if (resp.status === 'partial') {
        // Some moved, some refused (e.g. no delete rights in a Space): drop only the moved ones.
        const failed = new Set(resp.data?.failedIds ?? []);
        this.removeNotesLocally(this.selectedNoteIds.filter((id) => !failed.has(id)));
        this.clearSelectedNotes();
        this.alertService.warn(resp.data?.message || 'Some notes could not be moved to Trash.');
        return;
      }

      const errorMsg = resp.error?.message || 'Bulk delete failed.';
      this.gDS.showGlobalDialog('Bulk Delete Failed', 
        'Unable to delete notes.', errorMsg, DialogType.Error);
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error while deleting notes.';
      this.gDS.showGlobalDialog('Bulk Delete Failed', 
        'Unable to delete notes.', errorMsg, DialogType.Error);
    }
  }

  /** Grouped options for Bulk Actions → Add Tags (built when the dialog opens, not per change
   * detection): the user's private tags, plus the Space tags of the Spaces the selected notes are in. */
  bulkAddTagGroups: BulkTagOptionGroup[] = [];

  private buildBulkAddTagGroups(): BulkTagOptionGroup[] {
    const privateSpaceId = this.mySpaces.find((space) => space.type === 'PRIVATE')?.id;
    const selected = new Set(this.selectedNoteIds);
    const spacesInSelection = new Set(
      this.allNotes.filter((note) => selected.has(note._id)).map((note) => note.spaceId || privateSpaceId),
    );
    const active = this.filterTagCatalog.filter((tag) => tag.isActive !== false);
    return this.groupBulkTagOptions(
      active.filter((tag) => !isPrivateTag(tag) && spacesInSelection.has(tag.spaceId ?? undefined)),
      active.filter((tag) => isPrivateTag(tag)),
    );
  }

  /** Grouped options for Bulk Actions → Remove Tags: the tags actually on the selected notes — the ones
   * stored in each note (Space tags) and the user's private tags on them. */
  bulkRemoveTagGroups: BulkTagOptionGroup[] = [];

  private buildBulkRemoveTagGroups(): BulkTagOptionGroup[] {
    const selected = new Set(this.selectedNoteIds);
    // id → where it was found; an id the tag catalog doesn't know (e.g. a deleted tag) is classed by
    // that source, so a leftover private assignment is still offered — as a private tag.
    const present = new Map<string, 'note' | 'private'>();
    for (const note of this.allNotes) {
      if (!selected.has(note._id)) continue;
      for (const ref of note.Tags ?? []) present.set(typeof ref === 'string' ? ref : String(ref?._id ?? ''), 'note');
      for (const id of this.privateTagIndex?.get(note._id) ?? []) if (!present.has(id)) present.set(id, 'private');
    }
    const catalog = new Map(this.filterTagCatalog.map((tag) => [tag._id, tag]));
    const tags = [...present]
      .filter(([id]) => !!id)
      .map(
        ([id, source]) =>
          catalog.get(id) ??
          ({
            _id: id, type: 'tag', name: id, tagType: 'general', slug: id, parentTagId: null, isActive: true,
            scope: source === 'private' ? TAG_SCOPE.PRIVATE : TAG_SCOPE.SPACE,
          } as Tag),
      );
    return this.groupBulkTagOptions(
      tags.filter((tag) => !isPrivateTag(tag)),
      tags.filter((tag) => isPrivateTag(tag)),
    );
  }

  private groupBulkTagOptions(spaceTags: Tag[], privateTags: Tag[]): BulkTagOptionGroup[] {
    const option = (tag: Tag): BulkTagOption => ({
      _id: tag._id,
      name: tag.name,
      color: tag.color,
      isPrivate: isPrivateTag(tag),
      context: isPrivateTag(tag) ? 'Private' : this.spaceDirectory.getSpaceName(tag.spaceId ?? undefined),
    });
    const byName = (a: Tag, b: Tag) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const groups: BulkTagOptionGroup[] = [];
    if (spaceTags.length) groups.push({ label: 'Space tags', items: [...spaceTags].sort(byName).map(option) });
    if (privateTags.length) groups.push({ label: 'Private tags', items: [...privateTags].sort(byName).map(option) });
    return groups;
  }

  /**
   * Adds/removes private tags on many notes: ONE request to the bulk endpoint, or — on a server that
   * predates it — one request per note (a few at a time). Updates privateTagIndex so the list refreshes.
   * Notes the server has never received can't carry private tags; they're reported, not sent.
   */
  private async changePrivateTagsInBulk(
    op: 'add' | 'remove',
    notes: Note[],
    tagIds: string[],
  ): Promise<{ changedIds: Set<string>; failures: Array<{ note: Note; message: string }> }> {
    const changedIds = new Set<string>();
    const failures: Array<{ note: Note; message: string }> = [];
    const index = new Map(this.privateTagIndex ?? []);
    const eligible = notes.filter((note) => {
      if (note.localDraft || note.syncStatus === 'new') {
        failures.push({ note, message: 'not saved to the server yet' });
        return false;
      }
      return true;
    });
    if (!eligible.length) return { changedIds, failures };

    const outcome = await firstValueFrom(this.scopedTags.bulkMutatePrivateTags(op, eligible.map((n) => n._id), tagIds));
    if (outcome.kind === 'ok') {
      const byId = new Map(eligible.map((note) => [note._id, note]));
      for (const row of outcome.result.assignments) {
        const before = index.get(row.noteId) ?? [];
        index.set(row.noteId, row.privateTagIds);
        if (before.join('|') !== row.privateTagIds.join('|')) changedIds.add(row.noteId);
      }
      const noteFor = (id: string) => byId.get(id) ?? ({ _id: id, Title: id } as Note);
      outcome.result.notFound.forEach((id) => failures.push({ note: noteFor(id), message: 'not found on the server' }));
      outcome.result.conflicts.forEach((id) => failures.push({ note: noteFor(id), message: 'changed elsewhere at the same time — try again' }));
      outcome.result.failed.forEach((f) => failures.push({ note: noteFor(f.noteId), message: f.error }));
    } else if (outcome.kind === 'error') {
      eligible.forEach((note) => failures.push({ note, message: outcome.message }));
    } else {
      const queue = [...eligible];
      const worker = async () => {
        for (let note = queue.shift(); note; note = queue.shift()) {
          const resp = await firstValueFrom(
            op === 'add'
              ? this.scopedTags.addPrivateTags(note._id, tagIds, note.spaceId ?? null)
              : this.scopedTags.removePrivateTags(note._id, tagIds, note.spaceId ?? null),
          );
          if (resp.status === 'success') {
            index.set(note._id, resp.data ?? []);
            changedIds.add(note._id);
          } else {
            failures.push({ note, message: resp.error?.message || 'failed' });
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, eligible.length) }, worker));
    }
    this.privateTagIndex = index;
    return { changedIds, failures };
  }

  /**
   * Bulk Actions → Add Tags, scope-aware (tasks/frontend-tagging.md). The server's bulk-add endpoint
   * only handles notes/tags in the user's own database and rejects private tags, so:
   * - Space tags are added as ordinary local note edits (IndexedDB first, then the normal sync push —
   *   works offline too), and only to selected notes that are in the tag's own Space;
   * - private tags go through the bulk private-tag-assignment API (see changePrivateTagsInBulk).
   */
  private async handleBulkAddTags(tagIds?: string[]) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog('Bulk Action Unavailable', 'Select Mode needs to be enabled to run bulk actions.', '', DialogType.Error);
      return;
    }
    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog('No Notes Selected', 'Select one or more notes before adding tags.', '', DialogType.Error);
      return;
    }
    const normalizedTagIds = Array.from(new Set((tagIds || []).filter((id) => !!id)));
    if (!normalizedTagIds.length) {
      this.gDS.showGlobalDialog('Tags Required', 'Choose one or more tags before applying the bulk action.', '', DialogType.Error);
      return;
    }

    const catalog = new Map(this.filterTagCatalog.map((tag) => [tag._id, tag]));
    const chosen = normalizedTagIds.map((id) => catalog.get(id)).filter((tag): tag is Tag => !!tag);
    const privateTagIds = chosen.filter((tag) => isPrivateTag(tag)).map((tag) => tag._id);
    const spaceTags = chosen.filter((tag) => !isPrivateTag(tag));
    const privateSpaceId = this.mySpaces.find((space) => space.type === 'PRIVATE')?.id;
    const selected = new Set(this.selectedNoteIds);
    const notes = this.allNotes.filter((note) => selected.has(note._id));

    const updatedNoteIds = new Set<string>();
    const problems: string[] = [];
    const failedNoteIds = new Set<string>();
    const label = (note: Note) => note.Title || note._id;

    // Space tags: local edits, only within the tag's own Space.
    let spaceSkipped = 0;
    if (spaceTags.length) {
      for (const note of notes) {
        const noteSpaceId = note.spaceId || privateSpaceId;
        const applicable = spaceTags.filter((tag) => tag.spaceId === noteSpaceId).map((tag) => tag._id);
        if (!applicable.length) {
          spaceSkipped++;
          continue;
        }
        const existing = (note.Tags ?? []).map((t) => (typeof t === 'string' ? t : String(t?._id ?? ''))).filter(Boolean);
        const toAdd = applicable.filter((id) => !existing.includes(id));
        if (!toAdd.length) continue;
        try {
          await this.dataService.saveNoteTags({ ...note, Tags: [...existing, ...toAdd] });
          updatedNoteIds.add(note._id);
        } catch (err: any) {
          failedNoteIds.add(note._id);
          problems.push(`${label(note)}: ${err?.message || 'could not save'}`);
        }
      }
      if (updatedNoteIds.size && this.dataService.isOnline) {
        void this.syncCoordinator.forceIncrementalSync('auto');
      }
    }

    // Private tags: the assignment API (server-side only — needs a connection and a note the server has).
    if (privateTagIds.length) {
      if (!this.dataService.isOnline) {
        problems.push('Private tags can only be added while online.');
        notes.forEach((note) => failedNoteIds.add(note._id));
      } else {
        const { changedIds, failures } = await this.changePrivateTagsInBulk('add', notes, privateTagIds);
        changedIds.forEach((id) => updatedNoteIds.add(id));
        for (const { note, message } of failures) {
          failedNoteIds.add(note._id);
          problems.push(`${label(note)}: ${message}`);
        }
      }
    }

    this.applyFilters();
    // Not an error: a Space tag only belongs on notes in its own Space.
    const skippedNote =
      spaceSkipped && spaceTags.length ? ` Space tags were added only to notes in their own Space (${spaceSkipped} note(s) skipped).` : '';

    if (!problems.length) {
      this.alertService.success(`Added tags to ${updatedNoteIds.size} note(s).${skippedNote}`);
      this.clearSelectedNotes();
      return;
    }
    if (skippedNote) problems.push(skippedNote.trim());
    if (failedNoteIds.size) {
      this.selectedNoteIds = this.selectedNoteIds.filter((id) => failedNoteIds.has(id));
    } else {
      this.clearSelectedNotes();
    }
    this.gDS.showGlobalDialog(
      updatedNoteIds.size ? 'Tags Partly Added' : 'Tags Not Added',
      `Added tags to ${updatedNoteIds.size} note(s).` + (failedNoteIds.size ? ' The notes that failed are still selected.' : ''),
      problems.slice(0, 10).join('\n') + (problems.length > 10 ? `\n…and ${problems.length - 10} more` : ''),
      DialogType.Error,
    );
  }

  private async handleBulkRemoveTags(removeAllTags?: boolean, removeTagIds?: string[]) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before removing tags.',
        '',
        DialogType.Error,
      );
      return;
    }

    const removeAll = removeAllTags !== false;
    const chosen = new Set((removeTagIds || []).filter((id) => !!id));
    if (!removeAll && !chosen.size) {
      this.gDS.showGlobalDialog('Tags Required', 'Choose one or more tags before applying the bulk remove action.', '', DialogType.Error);
      return;
    }

    // Scope-aware (tasks/frontend-tagging.md): Space tags (stored in the note itself, Note.Tags) are
    // removed as ordinary local note edits, synced like any edit; the user's private tags go through
    // the bulk private-tag-assignment API. "Remove all" clears both.
    const selected = new Set(this.selectedNoteIds);
    const notes = this.allNotes.filter((note) => selected.has(note._id));
    const tagIdOf = (ref: string | Tag) => (typeof ref === 'string' ? ref : String(ref?._id ?? ''));
    const updatedNoteIds = new Set<string>();
    const failedNoteIds = new Set<string>();
    const problems: string[] = [];
    const label = (note: Note) => note.Title || note._id;

    let savedLocally = false;
    for (const note of notes) {
      const current = (note.Tags ?? []).map(tagIdOf).filter(Boolean);
      const remaining = removeAll ? [] : current.filter((id) => !chosen.has(id));
      if (remaining.length === current.length) continue;
      try {
        await this.dataService.saveNoteTags({ ...note, Tags: remaining });
        updatedNoteIds.add(note._id);
        savedLocally = true;
      } catch (err: any) {
        failedNoteIds.add(note._id);
        problems.push(`${label(note)}: ${err?.message || 'could not save'}`);
      }
    }
    if (savedLocally && this.dataService.isOnline) {
      void this.syncCoordinator.forceIncrementalSync('auto');
    }

    // Private tags: only notes that actually carry one of them.
    const privateIdsFor = (note: Note) =>
      (this.privateTagIndex?.get(note._id) ?? []).filter((id) => removeAll || chosen.has(id));
    const withPrivate = notes.filter((note) => privateIdsFor(note).length);
    if (withPrivate.length) {
      if (!this.dataService.isOnline) {
        problems.push('Private tags can only be removed while online.');
        withPrivate.forEach((note) => failedNoteIds.add(note._id));
      } else {
        const tagIds = [...new Set(withPrivate.flatMap(privateIdsFor))];
        const { changedIds, failures } = await this.changePrivateTagsInBulk('remove', withPrivate, tagIds);
        changedIds.forEach((id) => updatedNoteIds.add(id));
        for (const { note, message } of failures) {
          failedNoteIds.add(note._id);
          problems.push(`${label(note)}: ${message}`);
        }
      }
    }

    this.applyFilters();
    const done = removeAll
      ? `Removed all tags from ${updatedNoteIds.size} note(s).`
      : `Removed the selected tags from ${updatedNoteIds.size} note(s).`;
    if (!problems.length) {
      this.alertService.success(updatedNoteIds.size ? done : 'The selected notes had none of those tags.');
      this.clearSelectedNotes();
      return;
    }
    this.selectedNoteIds = this.selectedNoteIds.filter((id) => failedNoteIds.has(id));
    this.gDS.showGlobalDialog(
      updatedNoteIds.size ? 'Tags Partly Removed' : 'Tags Not Removed',
      done + ' The notes that failed are still selected.',
      problems.slice(0, 10).join('\n') + (problems.length > 10 ? `\n…and ${problems.length - 10} more` : ''),
      DialogType.Error,
    );
  }

  private async handleBulkPrintExport(mode: 'print', params?: BulkActionRequest['params']) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before exporting.',
        '',
        DialogType.Error,
      );
      return;
    }

    try {
      await this.notePrintExportService.printOrExportNotesByIds(
            this.selectedNoteIds, this.allNotes, mode, {
        fileName: params?.fileName,
        includeTableOfContents: params?.includeTableOfContents,
        pageBreakBetweenNotes: params?.pageBreakBetweenNotes,
        pageNumbers: params?.pageNumbers,
      });
      this.alertService.success('Print job created for selected notes.');
      this.clearSelectedNotes();
    } catch (err: any) {
      const message = err?.message || 'Failed to generate the PDF export.';
      this.gDS.showGlobalDialog('Bulk Export Failed',
        'Unable to export selected notes.', message, DialogType.Error);
    }
  }

  /** Reads the List view's persisted column-sort (sortBy/sortOrder) so the
   * export dialog's "Default (as current view)" option can replicate it,
   * regardless of which Home view is actually active right now — List is the
   * only view with an explicit, user-controlled sort order to mirror. */
  private getCurrentListSortState(): { sortBy: string; sortOrder: 'asc' | 'desc' } {
    return readListSortState();
  }

  private async handleBulkExport(params?: BulkActionRequest['params']) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before exporting.',
        '',
        DialogType.Error,
      );
      return;
    }

    const format: ExportFormat = params?.exportFormat || 'pdf';
    const sortOrder: ExportSortOrder = params?.sortOrder || 'default';

    try {
      const orderedIds = BulkActionsLogic.sortNoteIdsForExport(
        this.selectedNoteIds,
        this.allNotes,
        sortOrder,
        this.availableTags,
        this.getCurrentListSortState(),
        (note) => this.noteSizes.sortableSize(note),
      );
      const fileName = this.notePrintExportService.sanitizeFilenameForExtension(
        BulkActionsLogic.buildTimestampedExportBaseName(params?.fileName),
        `.${format}`,
      );

      if (format === 'pdf') {
        await this.notePrintExportService.printOrExportNotesByIds(orderedIds, this.allNotes, 'export', {
          fileName,
          includeTableOfContents: params?.includeTableOfContents,
          pageBreakBetweenNotes: params?.pageBreakBetweenNotes,
          pageNumbers: params?.pageNumbers,
        });
      } else if (format === 'docx') {
        await this.notePrintExportService.exportNotesToDocxByIds(orderedIds, this.allNotes, {
          fileName,
          includeTableOfContents: params?.includeTableOfContents,
          pageBreakBetweenNotes: params?.pageBreakBetweenNotes,
          pageNumbers: params?.pageNumbers,
          includeMetaData: params?.includeMetaData,
        });
      } else {
        const orderedNotes = orderedIds
          .map((id) => this.allNotes.find((note) => note._id === id))
          .filter((note): note is Note => !!note);
        const jsonStr = JSON.stringify(orderedNotes, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      this.alertService.success(`Exported selected notes to ${format.toUpperCase()}.`);
      this.clearSelectedNotes();
    } catch (err: any) {
      const message = err?.message || `Failed to generate the ${format.toUpperCase()} export.`;
      this.gDS.showGlobalDialog('Bulk Export Failed', 'Unable to export selected notes.', message, DialogType.Error);
    }
  }

  private applyBulkStatusLocally(status: string, updateTimestamp: boolean) {
    this.allNotes = BulkActionsLogic.applyBulkStatusLocally(
      this.allNotes,
      this.selectedNoteIds,
      status,
      updateTimestamp,
    );
    this.applyFilters();
  }

  private removeNotesLocally(noteIds: string[]) {
    this.allNotes = BulkActionsLogic.removeNotesLocally(this.allNotes, noteIds);
    this.applyFilters();
  }

  private applyBulkTagsLocally(tagIds: string[], noteIds: string[] = this.selectedNoteIds) {
    this.allNotes = BulkActionsLogic.applyBulkTagsLocally(
      this.allNotes,
      noteIds,
      tagIds,
    );
    this.applyFilters();
  }

  private applyBulkRemoveTagsLocally(removeAll: boolean, tagIds: string[], noteIds: string[] = this.selectedNoteIds) {
    this.allNotes = BulkActionsLogic.applyBulkRemoveTagsLocally(
      this.allNotes,
      noteIds,
      tagIds,
      removeAll,
    );
    this.applyFilters();
  }

  private getUpdatedCountFromBulk(
    data: BulkStatusResult | BulkDeleteResult | BulkTagAddResult | BulkTagRemoveResult | undefined,
    fallback: number,
  ): number {
    return BulkActionsLogic.getUpdatedCountFromBulk(data, fallback);
  }

  toggleZoomPanel(event: MouseEvent): void {
    event.stopPropagation();
    this.showZoomPanel = !this.showZoomPanel;
  }

  onSystemZoomChange(event: any): void {
    console.log('System zoom factor changed to', this.systemZoomFactor);
    this.userSettings.setLocal({ systemZoomFactor: this.systemZoomFactor });
    this.homeZoomScale = this.systemZoomFactor / 100;
  }

  persistSystemZoom(): void {
    this.userSettings.setLocal({ systemZoomFactor: this.systemZoomFactor });
  }
}
